import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import StaffHeader from '../components/StaffHeader';
import { inZone } from '../utils/companyDate';
import TimezoneSelect from '../components/TimezoneSelect';
import TimezoneSuggestion from '../components/TimezoneSuggestion';
import { useConfig } from '../context/ConfigContext';
import { formatPhoneDisplay, formatPhoneInput, stripPhone } from '../utils/phoneFormat';
import usePullToRefresh from '../hooks/usePullToRefresh';
import { PRODUCT_TYPE } from '../config/product';
import ConfirmModal from '../components/ConfirmModal';
import DepthPricingCard from '../components/DepthPricingCard';
import WelcomeMessageCard from '../components/WelcomeMessageCard';
import SeasonCheckinCard from '../components/SeasonCheckinCard';
import ServiceRequestsSetting from '../components/ServiceRequestsSetting';
import AddonFeeOptions, { addonFeesFromRow, addonFeeBody, defaultAddonFees } from '../components/AddonFeeOptions';
import SmsSettingsSection from '../components/SmsSettingsSection';
import { hasFeature } from '../config/features';
import LapsedBanner from '../components/LapsedBanner';
import { getPlanName, formatUsage } from '../config/plans';
import { SUPPORT_EMAIL } from '../config/support';
import {
  fetchBillingStatus,
  openBillingPortal,
  cancelSubscription,
  saveReturnMonth,
  billingErrorMessage,
} from '../utils/billing';
import { returnMonthOptions, formatReturnMonth, NOT_SURE } from '../utils/returnMonth';
import { feesById, feeFor, saveMethodsAndFees } from '../utils/paymentMethodFees';
import { DEFAULT_PAYMENT_METHODS, addCustomMethod, deleteCustomMethod, visibleMethods } from '../utils/paymentMethods';
import ProductEmailCard from '../components/ProductEmailCard';

// The same sentence the intake form and the customer record show, so the owner
// writing a definition is looking at the rule the definition has to satisfy.
const SIZE_FRAMING = 'Count parking spaces on the driveway surface, counting both width and length, not how many cars the customer owns.';

// Used only when companies.intake_size_confirmation_text is NULL, which is the
// state every company starts in. Editing the field replaces it for that company
// alone.
const DEFAULT_SIZE_CONFIRMATION = 'We confirm the size at your first visit. If it is larger than what you selected, we will tell you before we service it.';

// The Season dropdowns. Option value is the month number, 1-12.
const SEASON_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July',
  'August', 'September', 'October', 'November', 'December'];

// Subscription states that mean the company is paying us (or is about to).
// 'trial' is SnowTrack's own trial, tracked on storm_events_used rather than
// in Stripe, so it has no billing record to manage yet.
const BILLABLE_STATUSES = ['active', 'trialing', 'past_due'];
const ENDED_STATUSES = ['cancelled', 'canceled', 'expired'];

// A company can sit above a limit without having done anything wrong: it was
// over before the limits shipped, or it loaded 70 customers on a Pro trial and
// then bought Basic. That is allowed and nothing is taken away, but it should
// be visible here rather than discovered at the moment a create is refused.
// A null limit is unlimited and can never be exceeded.
const isOverLimit = (billing, kind) => {
  const limit = billing?.limits?.[kind];
  const used = billing?.usage?.[kind];
  if (limit === null || limit === undefined || used === undefined) return false;
  return used > limit;
};

const formatBillingDate = (value, timezone) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, inZone(timezone, { year: 'numeric', month: 'long', day: 'numeric' }));
};

// Plain-language subscription status. The raw values come from Stripe and are
// not something to show a customer as-is.
const BILLING_STATUS_LABELS = {
  active: 'Active',
  trialing: 'Trial (billing set up)',
  trial: 'Free trial',
  past_due: 'Payment failed',
  cancelled: 'Cancelled',
  canceled: 'Cancelled',
  expired: 'Expired',
  none: 'No subscription',
};

// The seven Settings groups, in page order, all collapsed. The group that
// opens by default depends on the role: see defaultSections.
const DEFAULT_SECTIONS = {
  company: false,
  pricing: false,
  customers: false,
  // Texting and Email
  messaging: false,
  // Invoicing and Payments. The Square OAuth return (?payments=) opens it.
  payments: false,
  team: false,
  account: false,
};

// Owners and admins land on Company; drivers and crew leads see only
// Account, so it opens for them.
const defaultSections = (admin) => ({
  ...DEFAULT_SECTIONS,
  [admin ? 'company' : 'account']: true,
});

// Saved open/closed state. The key is new with the seven groups, so the
// old per-section state (snowtrack_settings_sections, whose keys no longer
// exist) is dropped once and everyone starts from the default for their role.
const SECTIONS_STORAGE_KEY = 'snowtrack_settings_groups';

const loadSectionsState = (admin) => {
  try {
    localStorage.removeItem('snowtrack_settings_sections');
    const saved = localStorage.getItem(SECTIONS_STORAGE_KEY);
    if (saved) {
      return { ...defaultSections(admin), ...JSON.parse(saved) };
    }
  } catch (e) {
    console.error('Failed to load settings sections state:', e);
  }
  return defaultSections(admin);
};

// ?section= values from the Dashboard Getting started card and the Help
// Contact tab, and the group each one opens. Each value is also the suffix of
// the scroll target's id: settings-section-pricing, settings-section-intake
// and settings-section-account.
export const SECTION_LINKS = { pricing: 'pricing', intake: 'customers', account: 'account' };
function Settings() {
  const navigate = useNavigate();
  const { user, isAdmin, isOwner, refreshUser, effectiveTier, isLapsed, timezone } = useAuth();
  const { getAppName } = useConfig();

  // Collapsible sections state
  // ProtectedRoute renders Settings only once the user has loaded, so the
  // role is known here.
  const [expandedSections, setExpandedSections] = useState(() => loadSectionsState(isAdmin()));

  // Toggle section and persist to localStorage
  const toggleSection = useCallback((section) => {
    setExpandedSections(prev => {
      const updated = { ...prev, [section]: !prev[section] };
      try {
        localStorage.setItem(SECTIONS_STORAGE_KEY, JSON.stringify(updated));
      } catch (e) {
        console.error('Failed to save settings sections state:', e);
      }
      return updated;
    });
  }, []);
  // Profile form state
  const [profile, setProfile] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  });
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileSuccess, setProfileSuccess] = useState('');
  const [profileError, setProfileError] = useState('');
  // Asked for only while the email differs from the saved one: the server
  // requires it to change the login email.
  const [emailCurrentPassword, setEmailCurrentPassword] = useState('');
  const emailChanged = profile.email.trim().toLowerCase() !== (user?.email || '').trim().toLowerCase();

  // Password form state
  const [passwords, setPasswords] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [passwordError, setPasswordError] = useState('');

  // Company form state (for owners/admins)
  const [company, setCompany] = useState({
    name: '',
    // The number and address the customer portal's Contact Us card shows.
    // Loaded from GET /api/company, not from the user object, which carries
    // neither.
    businessPhone: '',
    businessEmail: '',
    // The postal address printed on customer statements. Kept in site_settings
    // (key business_address), not on companies, which has no address columns.
    businessAddress: '',
    // The company's IANA zone (companies.timezone). Every "today", quiet
    // hours, season edge and shown time follows it.
    timezone: '',
  });
  // The zones the select offers, from GET /api/company.
  const [timezoneOptions, setTimezoneOptions] = useState([]);
  const [companyLoading, setCompanyLoading] = useState(false);
  const [companySuccess, setCompanySuccess] = useState('');
  const [companyError, setCompanyError] = useState('');

  // SnowTrack subscription state (owner only). Distinct from the "Pricing &
  // Billing" section further down, which is about what this company charges its
  // own customers.
  const [billing, setBilling] = useState(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingError, setBillingError] = useState('');
  // Which billing request is in flight: 'portal' or 'cancel'.
  const [billingAction, setBillingAction] = useState(null);
  const [billingActionError, setBillingActionError] = useState('');
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  // The cancel modal's "When do you expect to be back?" choice, and the
  // Settings field that sets it later. '' is "Not sure".
  const [cancelReturnMonth, setCancelReturnMonth] = useState(NOT_SURE);
  const [returnMonthSaving, setReturnMonthSaving] = useState(false);
  const [returnMonthError, setReturnMonthError] = useState('');

  // Referral credit state (for owners/admins). Only these two keys: the
  // legacy tier_* keys the GET still returns have no reader, so they are no
  // longer held or sent back.
  const [pricing, setPricing] = useState({
    referral_discount: '5.00',
    minimum_price: '35.00',
  });
  const [pricingLoading, setPricingLoading] = useState(false);
  const [pricingSuccess, setPricingSuccess] = useState('');
  const [pricingError, setPricingError] = useState('');

  // Payment methods state
  const [paymentMethods, setPaymentMethods] = useState(DEFAULT_PAYMENT_METHODS);
  const [paymentMethodsLoading, setPaymentMethodsLoading] = useState(false);
  const [paymentMethodsSuccess, setPaymentMethodsSuccess] = useState('');
  const [paymentMethodsError, setPaymentMethodsError] = useState('');
  const [newMethodName, setNewMethodName] = useState('');

  // Square payment connection state.
  //
  // paymentsEnabled mirrors payment_connections_enabled from GET /api/company.
  // It is a PRESENTATION flag only: the server enforces the switch
  // independently on all six endpoints, so a client that ignored it would gain
  // nothing.
  const [paymentsEnabled, setPaymentsEnabled] = useState(false);
  // smsEnabled mirrors sms_enabled from GET /api/company, the same kind of
  // presentation flag: every /api/sms route enforces SMS_ENABLED itself.
  const [smsEnabled, setSmsEnabled] = useState(false);
  const [paymentConnection, setPaymentConnection] = useState(null);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [paymentsError, setPaymentsError] = useState('');
  const [paymentsSuccess, setPaymentsSuccess] = useState('');
  // Which payments request is in flight: 'connect', 'location' or 'disconnect'.
  const [paymentsAction, setPaymentsAction] = useState(null);
  const [squareLocations, setSquareLocations] = useState([]);
  const [squareLocationsLoaded, setSquareLocationsLoaded] = useState(false);
  const [selectedLocationId, setSelectedLocationId] = useState('');
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);

  // The fee for each payment method, keyed by method id, which is the
  // payment_method column of payment_method_fees. A fee row is on exactly
  // when its method is turned on. A method with no saved row shows 0% + $0,
  // except Square, which shows its EMAILED-INVOICE rate, 3.30% + $0.30
  // (confirmed from a real receipt; 2.6% + $0.10 is the card-present rate).
  // That matches the server fallback, migration 065's seed and the rate the
  // Expenses booking uses when the Square row is off or missing.
  const [methodFees, setMethodFees] = useState({});

  // Lead sources state
  const [leadSources, setLeadSources] = useState([
    { id: 'facebook', name: 'Facebook', enabled: true, isBuiltIn: true },
    { id: 'craigslist', name: 'Craigslist', enabled: true, isBuiltIn: true },
    { id: 'flyer', name: 'Flyer', enabled: true, isBuiltIn: true },
    { id: 'yard_sign', name: 'Yard Sign', enabled: true, isBuiltIn: true },
    { id: 'referral', name: 'Friend/Referral', enabled: true, isBuiltIn: true },
    { id: 'google', name: 'Google', enabled: true, isBuiltIn: true },
    { id: 'nextdoor', name: 'Nextdoor', enabled: true, isBuiltIn: true },
    { id: 'other', name: 'Other', enabled: true, isBuiltIn: true },
  ]);
  const [leadSourcesLoading, setLeadSourcesLoading] = useState(false);
  const [leadSourcesSuccess, setLeadSourcesSuccess] = useState('');
  const [leadSourcesError, setLeadSourcesError] = useState('');
  const [newSourceName, setNewSourceName] = useState('');

  // Company code for intake form
  const [companyCode, setCompanyCode] = useState('');
  const [copiedLink, setCopiedLink] = useState(false);

  // Custom slug state
  const [customSlug, setCustomSlug] = useState('');
  const [customSlugInput, setCustomSlugInput] = useState('');
  const [slugAvailable, setSlugAvailable] = useState(null); // null = not checked, true/false = available
  const [slugChecking, setSlugChecking] = useState(false);
  const [slugSaving, setSlugSaving] = useState(false);
  const [slugError, setSlugError] = useState('');
  const [slugSuccess, setSlugSuccess] = useState('');
  const [slugCheckTimeout, setSlugCheckTimeout] = useState(null);

  // Lead source stats
  const [leadSourceStats, setLeadSourceStats] = useState({ stats: [], total: 0 });
  const [leadSourceStatsPeriod, setLeadSourceStatsPeriod] = useState('all');
  const [leadSourceStatsLoading, setLeadSourceStatsLoading] = useState(false);

  // Invoice defaults state
  const [invoiceDefaults, setInvoiceDefaults] = useState({
    defaultInvoiceTitle: '{route_name}',
    defaultInvoiceMessage: 'Thank you for your business!',
    autoInvoiceSquare: false,
  });
  const [invoiceDefaultsLoading, setInvoiceDefaultsLoading] = useState(false);
  const [invoiceDefaultsSuccess, setInvoiceDefaultsSuccess] = useState('');
  const [invoiceDefaultsError, setInvoiceDefaultsError] = useState('');

  // Pricing tiers state
  const [pricingTiers, setPricingTiers] = useState([]);
  // Bumped on every tier reload, so the depth pricing card re-reads its tiers.
  const [pricingTiersVersion, setPricingTiersVersion] = useState(0);
  const [pricingTiersLoading, setPricingTiersLoading] = useState(false);
  const [pricingTiersSuccess, setPricingTiersSuccess] = useState('');
  const [pricingTiersError, setPricingTiersError] = useState('');
  const [tiersModified, setTiersModified] = useState(false);

  // Feature request state
  const [featureRequest, setFeatureRequest] = useState({ subject: '', description: '' });
  const [featureRequestLoading, setFeatureRequestLoading] = useState(false);
  const [featureRequestSuccess, setFeatureRequestSuccess] = useState('');
  const [featureRequestError, setFeatureRequestError] = useState('');

  // My feature requests state
  const [myFeatureRequests, setMyFeatureRequests] = useState([]);
  const [myFeatureRequestsLoading, setMyFeatureRequestsLoading] = useState(false);

  // Account deletion state (audit C6 / Finding 4.8a). Owner-only, type-to-
  // confirm, and irreversible - same pattern as the super admin company
  // delete in AdminPanel.js.
  const [deleteAccountConfirm, setDeleteAccountConfirm] = useState('');
  const [deleteAccountLoading, setDeleteAccountLoading] = useState(false);
  const [deleteAccountError, setDeleteAccountError] = useState('');

  // Weather location state
  const [weatherSettings, setWeatherSettings] = useState({
    weatherUseHome: false,
    weatherCity: '',
    weatherState: '',
  });
  const [weatherSettingsLoading, setWeatherSettingsLoading] = useState(false);
  const [weatherSettingsSuccess, setWeatherSettingsSuccess] = useState('');
  const [weatherSettingsError, setWeatherSettingsError] = useState('');
  // Set after each successful save with a home city: TimezoneSuggestion then
  // checks the NWS zone for it. Null hides the suggestion.
  const [timezoneCheck, setTimezoneCheck] = useState(null);

  // Season months (1-12), companies.season_start_month / season_end_month.
  // Defaults match migration 089 until GET /api/company answers.
  const [seasonSettings, setSeasonSettings] = useState({ startMonth: 10, endMonth: 4 });
  const [seasonSettingsLoading, setSeasonSettingsLoading] = useState(false);
  const [seasonSettingsSuccess, setSeasonSettingsSuccess] = useState('');
  const [seasonSettingsError, setSeasonSettingsError] = useState('');

  // Intake disclaimer state
  //
  // There is no default disclaimer. A NULL column shows as an empty field,
  // because an empty field is exactly what the public form renders: no terms
  // block at all. This used to be filled with a mechanic's lien clause, so a
  // contractor who never saved saw lien terms here while their customers were
  // shown nothing. Payment terms are the contractor's to write.
  //
  // The starter is inserted only when the owner presses "Use a basic starter",
  // and only into an empty field. It is never loaded, prefilled or saved on
  // its own.
  const STARTER_DISCLAIMER = 'Payment for services is due upon receipt of invoice. {company_name} reserves the right to pursue collection of unpaid balances.';

  // companies.show_public_pricing; the server treats null as shown.
  const [showPublicPricing, setShowPublicPricing] = useState(true);
  const [publicPricingSaving, setPublicPricingSaving] = useState(false);
  const [publicPricingMessage, setPublicPricingMessage] = useState({ type: '', text: '' });
  const [disclaimerSettings, setDisclaimerSettings] = useState({
    enabled: true,
    text: '',
    sizeConfirmationText: DEFAULT_SIZE_CONFIRMATION,
  });
  const [disclaimerSettingsLoading, setDisclaimerSettingsLoading] = useState(false);
  const [disclaimerSettingsSuccess, setDisclaimerSettingsSuccess] = useState('');
  const [disclaimerSettingsError, setDisclaimerSettingsError] = useState('');

  // Route optimization state
  const [routeSettings, setRouteSettings] = useState({
    useGps: true,
  });
  const [routeSettingsLoading, setRouteSettingsLoading] = useState(false);
  const [routeSettingsSuccess, setRouteSettingsSuccess] = useState('');
  const [routeSettingsError, setRouteSettingsError] = useState('');

  // Expense categories state
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [expenseCategoriesLoading, setExpenseCategoriesLoading] = useState(false);
  const [expenseCategoriesSuccess, setExpenseCategoriesSuccess] = useState('');
  const [expenseCategoriesError, setExpenseCategoriesError] = useState('');
  const [newCategoryName, setNewCategoryName] = useState('');

  // Service add-ons state
  const [serviceAddons, setServiceAddons] = useState([]);
  const [serviceAddonsLoading, setServiceAddonsLoading] = useState(false);
  const [serviceAddonsSuccess, setServiceAddonsSuccess] = useState('');
  const [serviceAddonsError, setServiceAddonsError] = useState('');
  const [newAddonName, setNewAddonName] = useState('');
  const [newAddonPrice, setNewAddonPrice] = useState('');
  const [newAddonFees, setNewAddonFees] = useState(defaultAddonFees);
  const [addonTierPrices, setAddonTierPrices] = useState({}); // { addonId: { tierId: price } }

  // Team management state
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamInvitations, setTeamInvitations] = useState([]);
  const [teamLoading, setTeamLoading] = useState(false);
  const [teamError, setTeamError] = useState('');
  const [teamSuccess, setTeamSuccess] = useState('');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('driver');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState('');
  // True when the invite was refused by the plan user limit rather than by a
  // duplicate address or a server fault, so only that case offers the way out.
  const [inviteErrorIsLimit, setInviteErrorIsLimit] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(null);

  // US States list
  const US_STATES = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
  ];

  const loadMyFeatureRequests = useCallback(async () => {
    setMyFeatureRequestsLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/feature-requests/mine`);
      setMyFeatureRequests(response.data);
    } catch (err) {
      console.error('Failed to load feature requests:', err);
    } finally {
      setMyFeatureRequestsLoading(false);
    }
  }, []);

  // Team management functions
  const loadTeamMembers = useCallback(async () => {
    setTeamLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/team`);
      setTeamMembers(response.data);
    } catch (err) {
      console.error('Failed to load team members:', err);
      setTeamError('Failed to load team members');
    } finally {
      setTeamLoading(false);
    }
  }, []);

  const loadTeamInvitations = useCallback(async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/team/invitations`);
      setTeamInvitations(response.data);
    } catch (err) {
      // Non-admins will get 403, which is expected
      if (err.response?.status !== 403) {
        console.error('Failed to load invitations:', err);
      }
    }
  }, []);

  const handleSendInvite = async (e) => {
    e.preventDefault();
    setInviteError('');
    setInviteErrorIsLimit(false);
    setInviteLoading(true);

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/team/invite`, {
        email: inviteEmail,
        role: inviteRole,
      });
      // The server creates the invitation even when the email cannot be sent,
      // and hands back the join link so it can be shared by hand.
      const emailFailed = response.data?.inviteSent === false && response.data?.inviteUrl;
      if (emailFailed) {
        setTeamSuccess(`Invitation created but the email could not be sent. Share this link: ${response.data.inviteUrl}`);
      } else {
        setTeamSuccess('Invitation sent successfully!');
      }
      setShowInviteModal(false);
      setInviteEmail('');
      setInviteRole('driver');
      loadTeamInvitations();
      // Leave the fallback link up long enough to copy
      setTimeout(() => setTeamSuccess(''), emailFailed ? 60000 : 3000);
    } catch (err) {
      setInviteError(err.response?.data?.error || 'Failed to send invitation');
      setInviteErrorIsLimit(err.response?.data?.code === 'USER_LIMIT_REACHED');
    } finally {
      setInviteLoading(false);
    }
  };

  const handleUpdateRole = async (userId, newRole) => {
    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/api/team/${userId}`, { role: newRole });
      setTeamSuccess('Role updated successfully!');
      setEditingMember(null);
      loadTeamMembers();
      setTimeout(() => setTeamSuccess(''), 3000);
    } catch (err) {
      setTeamError(err.response?.data?.error || 'Failed to update role');
      setTimeout(() => setTeamError(''), 3000);
    }
  };

  const handleRemoveMember = async (userId) => {
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/team/${userId}`);
      setTeamSuccess('Team member removed');
      setConfirmRemove(null);
      loadTeamMembers();
      setTimeout(() => setTeamSuccess(''), 3000);
    } catch (err) {
      setTeamError(err.response?.data?.error || 'Failed to remove team member');
      setTimeout(() => setTeamError(''), 3000);
    }
  };

  const handleCancelInvitation = async (invitationId) => {
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/team/invitations/${invitationId}`);
      setTeamSuccess('Invitation cancelled');
      loadTeamInvitations();
      setTimeout(() => setTeamSuccess(''), 3000);
    } catch (err) {
      setTeamError(err.response?.data?.error || 'Failed to cancel invitation');
      setTimeout(() => setTeamError(''), 3000);
    }
  };

  const formatRole = (role) => {
    const roleMap = {
      owner: 'Owner',
      admin: 'Admin',
      crew_lead: 'Crew Lead',
      driver: 'Driver',
    };
    return roleMap[role] || role;
  };

  const getRoleBadgeClass = (role) => {
    const classMap = {
      owner: 'badge-owner',
      admin: 'badge-admin',
      crew_lead: 'badge-crew-lead',
      driver: 'badge-driver',
    };
    return classMap[role] || '';
  };

  const loadPricingTiers = useCallback(async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/pricing-tiers/all?product=${PRODUCT_TYPE}`);
      setPricingTiers(response.data.map(t => ({
        ...t,
        default_price: parseFloat(t.default_price).toFixed(2),
        salt_price: parseFloat(t.salt_price || 0).toFixed(2)
      })));
      setTiersModified(false);
      setPricingTiersVersion((v) => v + 1);
    } catch (err) {
      console.error('Failed to load pricing tiers:', err);
    }
  }, []);

  const loadPricingSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/settings/pricing`);
      setPricing({
        referral_discount: response.data.referral_discount,
        minimum_price: response.data.minimum_price,
      });
    } catch (err) {
      console.error('Failed to load pricing settings:', err);
    }
  }, []);

  const loadMethodFees = useCallback(async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/payment-method-fees`);
      if (Array.isArray(response.data)) setMethodFees(feesById(response.data));
    } catch (err) {
      console.error('Failed to load processing fees:', err);
    }
  }, []);

  const handleMethodFeeChange = (methodId, field, value) => {
    setMethodFees(prev => ({ ...prev, [methodId]: { ...feeFor(prev, methodId), [field]: parseFloat(value) || 0 } }));
    setPaymentMethodsSuccess('');
    setPaymentMethodsError('');
  };

  const loadPaymentMethods = useCallback(async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/settings/payment-methods`);
      if (response.data && response.data.length > 0) {
        setPaymentMethods(response.data);
      }
    } catch (err) {
      console.error('Failed to load payment methods:', err);
    }
  }, []);

  const handlePaymentMethodToggle = (methodId) => {
    setPaymentMethods(prev =>
      prev.map(m => m.id === methodId ? { ...m, enabled: !m.enabled } : m)
    );
    setPaymentMethodsSuccess('');
    setPaymentMethodsError('');
  };

  // The id rules and the restore of a deleted method of the same name live in
  // utils/paymentMethods; the server applies the same rules on save.
  const handleAddPaymentMethod = () => {
    if (!newMethodName.trim()) return;
    const result = addCustomMethod(paymentMethods, newMethodName);
    if (result.error) {
      setPaymentMethodsError(result.error);
      return;
    }
    setPaymentMethods(result.methods);
    setNewMethodName('');
    setPaymentMethodsSuccess('');
    setPaymentMethodsError('');
  };

  // A soft delete: the method stays in the saved list, off and hidden here,
  // so the stops paid with it keep its name and its fee row is saved off.
  const handleDeletePaymentMethod = (methodId) => {
    setPaymentMethods(prev => deleteCustomMethod(prev, methodId));
    setPaymentMethodsSuccess('');
    setPaymentMethodsError('');
  };

  const handlePaymentMethodsSubmit = async (e) => {
    e.preventDefault();
    setPaymentMethodsLoading(true);
    setPaymentMethodsError('');
    setPaymentMethodsSuccess('');

    // The methods, then one fee row per method; see utils/paymentMethodFees.
    const result = await saveMethodsAndFees(axios.put, process.env.REACT_APP_API_URL, paymentMethods, methodFees);
    if (result.error) setPaymentMethodsError(result.error);
    else setPaymentMethodsSuccess(result.success);
    setPaymentMethodsLoading(false);
  };

  // Lead sources functions
  const loadLeadSources = useCallback(async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/settings/lead-sources`);
      if (response.data && response.data.length > 0) {
        setLeadSources(response.data);
      }
    } catch (err) {
      console.error('Failed to load lead sources:', err);
    }
  }, []);

  const loadExpenseCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/expense-categories?includeInactive=true`);
      if (response.data?.categories) {
        setExpenseCategories(response.data.categories.map(cat => ({
          id: cat.id,
          name: cat.name,
          enabled: cat.is_active,
          isBuiltIn: cat.is_default,
          sortOrder: cat.sort_order,
        })));
      }
    } catch (err) {
      console.error('Failed to load expense categories:', err);
    }
  }, []);

  const loadServiceAddons = useCallback(async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/service-addons/all?product=${PRODUCT_TYPE}`);
      if (response.data) {
        setServiceAddons(response.data.map(addon => ({
          id: addon.id,
          name: addon.name,
          price: parseFloat(addon.price) || 0,
          enabled: addon.is_active,
          sortOrder: addon.sort_order,
          fees: addonFeesFromRow(addon),
        })));
        const tierMap = {};
        for (const addon of response.data) {
          if (addon.tier_prices && addon.tier_prices.length > 0) {
            tierMap[addon.id] = {};
            for (const tp of addon.tier_prices) {
              tierMap[addon.id][tp.pricing_tier_id] = tp.price;
            }
          }
        }
        setAddonTierPrices(tierMap);
      }
    } catch (err) {
      console.error('Failed to load service add-ons:', err);
    }
  }, []);

  const loadCompanyCode = useCallback(async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/settings/company-code`);
      if (response.data?.companyCode) {
        setCompanyCode(response.data.companyCode);
      }
    } catch (err) {
      console.error('Failed to load company code:', err);
    }
  }, []);

  // Subscription billing. Owner only, matching the gates on the portal and
  // cancel routes - an admin who loaded this would just collect 403s.
  const loadBillingStatus = useCallback(async () => {
    setBillingLoading(true);
    try {
      const data = await fetchBillingStatus();
      setBilling(data);
      setBillingError('');
    } catch (err) {
      console.error('Failed to load billing status:', err);
      setBillingError(billingErrorMessage(err, 'Could not load your subscription details.'));
    } finally {
      setBillingLoading(false);
    }
  }, []);

  const handleManageBilling = async () => {
    setBillingActionError('');
    setBillingAction('portal');
    try {
      await openBillingPortal();
    } catch (err) {
      console.error('Billing portal failed:', err);
      setBillingActionError(
        billingErrorMessage(err, 'Could not open the billing portal. Please try again.')
      );
      setBillingAction(null);
    }
  };

  const handleCancelSubscription = async () => {
    setShowCancelConfirm(false);
    setBillingActionError('');
    setBillingAction('cancel');
    try {
      await cancelSubscription(cancelReturnMonth || null);
      setCancelReturnMonth(NOT_SURE);
      // Re-read rather than patching state locally: the cancel route returns
      // Stripe's cancel_at, but the columns this screen renders are written by
      // the webhook, and re-reading keeps the two from disagreeing.
      await loadBillingStatus();
    } catch (err) {
      console.error('Cancel subscription failed:', err);
      setBillingActionError(
        billingErrorMessage(err, 'Could not cancel the subscription. Please try again.')
      );
    } finally {
      setBillingAction(null);
    }
  };

  // Saved as soon as it is picked. The server answers 409 unless a cancel is
  // pending or the subscription has ended, which is also when this shows.
  const handleReturnMonthChange = async (value) => {
    setReturnMonthError('');
    setReturnMonthSaving(true);
    try {
      const data = await saveReturnMonth(value || null);
      setBilling((prev) => (prev ? { ...prev, returnMonth: data.returnMonth } : prev));
    } catch (err) {
      console.error('Save return month failed:', err);
      setReturnMonthError(billingErrorMessage(err, 'Could not save your return month. Please try again.'));
    } finally {
      setReturnMonthSaving(false);
    }
  };

  const loadCompanyInfo = useCallback(async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/company`);
      if (response.data?.custom_slug) {
        setCustomSlug(response.data.custom_slug);
        setCustomSlugInput(response.data.custom_slug);
      }
      // Load invoice defaults
      if (response.data) {
        setCompany((prev) => ({
          ...prev,
          businessPhone: formatPhoneDisplay(response.data.business_phone) || '',
          businessEmail: response.data.business_email || '',
          businessAddress: response.data.business_address || '',
          timezone: response.data.timezone || '',
        }));
        setTimezoneOptions(Array.isArray(response.data.timezone_options) ? response.data.timezone_options : []);
        setShowPublicPricing(response.data.show_public_pricing !== false);
        setInvoiceDefaults({
          defaultInvoiceTitle: response.data.default_invoice_title || '{route_name}',
          defaultInvoiceMessage: response.data.default_invoice_message || 'Thank you for your business!',
          autoInvoiceSquare: response.data.auto_invoice_square || false,
        });
        // Load weather settings
        setWeatherSettings({
          weatherUseHome: response.data.weather_use_home || false,
          weatherCity: response.data.weather_city || '',
          weatherState: response.data.weather_state || '',
        });
        // Load disclaimer settings
        setDisclaimerSettings({
          enabled: response.data.intake_disclaimer_enabled ?? true,
          text: response.data.intake_disclaimer_text || '',
          // NULL is the state every company starts in, so the field shows the
          // default line rather than an empty box. Saving it as it stands is
          // then an explicit adoption of that text, which is the same pattern
          // the disclaimer above uses.
          sizeConfirmationText: response.data.intake_size_confirmation_text || DEFAULT_SIZE_CONFIRMATION,
        });
        // Load the season. An older server without the columns leaves the default.
        if (response.data.season_start_month && response.data.season_end_month) {
          setSeasonSettings({
            startMonth: Number(response.data.season_start_month),
            endMonth: Number(response.data.season_end_month),
          });
        }
        // Load route optimization settings
        setRouteSettings({
          useGps: response.data.route_optimize_use_gps ?? true,
        });
        // Whether the Square Account card exists at all. A backend that does not
        // send the key is treated as "off", which is the safe default and is
        // also correct against an older server.
        setPaymentsEnabled(response.data.payment_connections_enabled === true);
        setSmsEnabled(response.data.sms_enabled === true);
      }
    } catch (err) {
      console.error('Failed to load company info:', err);
    }
  }, []);

  // The Square connection this company has, or null.
  //
  // A 404 means the feature is switched off server side and is NOT surfaced as
  // an error - that keeps the page correct even if the flag key is missing
  // from the API response.
  const loadPaymentConnections = useCallback(async () => {
    setPaymentsLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/payments/connections`);
      const connections = response.data?.connections || [];
      const square = connections.find((c) => c.provider === 'square') || null;
      setPaymentConnection(square);
      setSelectedLocationId(square?.locationId || '');
      setPaymentsError('');
    } catch (err) {
      if (err.response?.status === 404) {
        setPaymentConnection(null);
        setPaymentsEnabled(false);
      } else {
        setPaymentsError('Could not load your payment connections.');
      }
    } finally {
      setPaymentsLoading(false);
    }
  }, []);

  const loadLeadSourceStats = useCallback(async (period = 'all') => {
    setLeadSourceStatsLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/settings/lead-source-stats?period=${period}&product=${PRODUCT_TYPE}`
      );
      setLeadSourceStats(response.data);
    } catch (err) {
      console.error('Failed to load lead source stats:', err);
    } finally {
      setLeadSourceStatsLoading(false);
    }
  }, []);

  const handleLeadSourceToggle = (sourceId) => {
    setLeadSources(prev =>
      prev.map(s => s.id === sourceId ? { ...s, enabled: !s.enabled } : s)
    );
    setLeadSourcesSuccess('');
    setLeadSourcesError('');
  };

  const handleAddLeadSource = () => {
    if (!newSourceName.trim()) return;
    const id = newSourceName.toLowerCase().replace(/\s+/g, '_');
    if (leadSources.some(s => s.id === id)) {
      setLeadSourcesError('A lead source with that name already exists');
      return;
    }
    setLeadSources(prev => [...prev, { id, name: newSourceName.trim(), enabled: true, isBuiltIn: false }]);
    setNewSourceName('');
    setLeadSourcesSuccess('');
    setLeadSourcesError('');
  };

  const handleDeleteLeadSource = (sourceId) => {
    setLeadSources(prev => prev.filter(s => s.id !== sourceId));
    setLeadSourcesSuccess('');
    setLeadSourcesError('');
  };

  const handleLeadSourcesSubmit = async (e) => {
    e.preventDefault();
    setLeadSourcesLoading(true);
    setLeadSourcesError('');
    setLeadSourcesSuccess('');

    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/api/settings/lead-sources`, { sources: leadSources });
      setLeadSourcesSuccess('Lead sources updated successfully');
    } catch (err) {
      setLeadSourcesError(err.response?.data?.error || 'Failed to update lead sources');
    } finally {
      setLeadSourcesLoading(false);
    }
  };

  // Expense category handlers
  const handleExpenseCategoryToggle = (categoryId) => {
    setExpenseCategories(prev =>
      prev.map(c => c.id === categoryId ? { ...c, enabled: !c.enabled } : c)
    );
    setExpenseCategoriesSuccess('');
    setExpenseCategoriesError('');
  };

  const handleAddExpenseCategory = async () => {
    if (!newCategoryName.trim()) return;

    if (expenseCategories.some(c => c.name.toLowerCase() === newCategoryName.trim().toLowerCase())) {
      setExpenseCategoriesError('A category with that name already exists');
      return;
    }

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/expense-categories`, {
        name: newCategoryName.trim()
      });

      if (response.data?.category) {
        setExpenseCategories(prev => [...prev, {
          id: response.data.category.id,
          name: response.data.category.name,
          enabled: true,
          isBuiltIn: false,
          sortOrder: response.data.category.sort_order,
        }]);
        setNewCategoryName('');
        setExpenseCategoriesSuccess('Category added');
        setTimeout(() => setExpenseCategoriesSuccess(''), 2000);
      }
    } catch (err) {
      setExpenseCategoriesError(err.response?.data?.error || 'Failed to add category');
    }
  };

  const handleDeleteExpenseCategory = async (categoryId) => {
    const category = expenseCategories.find(c => c.id === categoryId);
    if (category?.isBuiltIn) {
      setExpenseCategoriesError('Cannot delete built-in categories. You can disable them instead.');
      return;
    }

    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/expense-categories/${categoryId}`);
      setExpenseCategories(prev => prev.filter(c => c.id !== categoryId));
      setExpenseCategoriesSuccess('Category deleted');
      setTimeout(() => setExpenseCategoriesSuccess(''), 2000);
    } catch (err) {
      setExpenseCategoriesError(err.response?.data?.error || 'Failed to delete category');
    }
  };

  const handleExpenseCategoriesSubmit = async (e) => {
    e.preventDefault();
    setExpenseCategoriesLoading(true);
    setExpenseCategoriesError('');
    setExpenseCategoriesSuccess('');

    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/api/expense-categories`, {
        categories: expenseCategories.map(c => ({
          id: c.id,
          is_active: c.enabled,
          sort_order: c.sortOrder,
        }))
      });
      setExpenseCategoriesSuccess('Expense categories updated successfully');
    } catch (err) {
      setExpenseCategoriesError(err.response?.data?.error || 'Failed to update expense categories');
    } finally {
      setExpenseCategoriesLoading(false);
    }
  };

  // Service add-on handlers
  const handleServiceAddonToggle = (addonId) => {
    setServiceAddons(prev =>
      prev.map(a => a.id === addonId ? { ...a, enabled: !a.enabled } : a)
    );
    setServiceAddonsSuccess('');
    setServiceAddonsError('');
  };

  const handleAddonNameChange = (addonId, newName) => {
    setServiceAddons(prev =>
      prev.map(a => a.id === addonId ? { ...a, name: newName } : a)
    );
    setServiceAddonsSuccess('');
    setServiceAddonsError('');
  };

  const handleAddonPriceChange = (addonId, newPrice) => {
    setServiceAddons(prev =>
      prev.map(a => a.id === addonId ? { ...a, price: parseFloat(newPrice) || 0 } : a)
    );
    setServiceAddonsSuccess('');
    setServiceAddonsError('');
  };

  const handleAddonTierPriceChange = (addonId, tierId, newPrice) => {
    setAddonTierPrices(prev => ({
      ...prev,
      [addonId]: {
        ...(prev[addonId] || {}),
        [tierId]: newPrice === '' ? '' : parseFloat(newPrice) || 0,
      }
    }));
    setServiceAddonsSuccess('');
    setServiceAddonsError('');
  };

  const handleAddonFeesChange = (addonId, patch) => {
    if (addonId === 'new') {
      setNewAddonFees(prev => ({ ...prev, ...patch }));
    } else {
      setServiceAddons(prev =>
        prev.map(a => a.id === addonId ? { ...a, fees: { ...a.fees, ...patch } } : a)
      );
    }
    setServiceAddonsSuccess('');
    setServiceAddonsError('');
  };

  const handleAddServiceAddon = async () => {
    if (!newAddonName.trim()) { setServiceAddonsError('Please enter an add-on name'); return; }
    if (serviceAddons.some(a => a.name.toLowerCase() === newAddonName.trim().toLowerCase())) {
      setServiceAddonsError('An add-on with that name already exists'); return;
    }
    try {
      const newTierPrices = [];
      if (addonTierPrices['new']) {
        for (const [tierId, price] of Object.entries(addonTierPrices['new'])) {
          if (price !== '' && price !== null && price !== undefined) {
            newTierPrices.push({ pricing_tier_id: parseInt(tierId), price: parseFloat(price) || 0 });
          }
        }
      }
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/service-addons`, {
        name: newAddonName.trim(), price: parseFloat(newAddonPrice) || 0, productType: PRODUCT_TYPE, tierPrices: newTierPrices,
        ...addonFeeBody(newAddonFees),
      });
      if (response.data) {
        setServiceAddons(prev => [...prev, {
          id: response.data.id, name: response.data.name, price: parseFloat(response.data.price) || 0, enabled: true, sortOrder: response.data.sort_order,
          fees: addonFeesFromRow(response.data),
        }]);
        if (addonTierPrices['new']) {
          setAddonTierPrices(prev => { const updated = { ...prev }; updated[response.data.id] = prev['new']; delete updated['new']; return updated; });
        }
        setNewAddonName(''); setNewAddonPrice(''); setNewAddonFees(defaultAddonFees());
        setServiceAddonsSuccess('Add-on created'); setTimeout(() => setServiceAddonsSuccess(''), 2000);
      }
    } catch (err) { setServiceAddonsError(err.response?.data?.error || 'Failed to add service add-on'); }
  };

  const handleDeleteServiceAddon = async (addonId) => {
    if (!window.confirm('Delete this add-on? If it has been used on any stops, it will be deactivated instead.')) {
      return;
    }
    try {
      const response = await axios.delete(`${process.env.REACT_APP_API_URL}/api/service-addons/${addonId}`);
      if (response.data?.softDeleted) {
        setServiceAddons(prev =>
          prev.map(a => a.id === addonId ? { ...a, enabled: false } : a)
        );
        setServiceAddonsSuccess('Add-on deactivated (it was used on previous stops)');
      } else {
        setServiceAddons(prev => prev.filter(a => a.id !== addonId));
        setServiceAddonsSuccess('Add-on deleted');
      }
      setTimeout(() => setServiceAddonsSuccess(''), 2000);
    } catch (err) {
      setServiceAddonsError(err.response?.data?.error || 'Failed to delete add-on');
    }
  };

  const handleServiceAddonsSubmit = async (e) => {
    e.preventDefault();
    setServiceAddonsLoading(true);
    setServiceAddonsError('');
    setServiceAddonsSuccess('');
    let saving = null;
    try {
      for (const addon of serviceAddons) {
        saving = addon;
        const tierPricesForAddon = [];
        if (addonTierPrices[addon.id]) {
          for (const [tierId, price] of Object.entries(addonTierPrices[addon.id])) {
            if (price !== '' && price !== null && price !== undefined) {
              tierPricesForAddon.push({ pricing_tier_id: parseInt(tierId), price: parseFloat(price) || 0 });
            }
          }
        }
        await axios.put(`${process.env.REACT_APP_API_URL}/api/service-addons/${addon.id}`, {
          name: addon.name, price: addon.price, isActive: addon.enabled, sortOrder: addon.sortOrder,
          tierPrices: tierPricesForAddon,
          ...addonFeeBody(addon.fees),
        });
      }
      setServiceAddonsSuccess('Service add-ons updated successfully');
      setTimeout(() => setServiceAddonsSuccess(''), 3000);
    } catch (err) {
      const message = err.response?.data?.error || 'Failed to update service add-ons';
      setServiceAddonsError(saving ? `${saving.name}: ${message}` : message);
    } finally {
      setServiceAddonsLoading(false);
    }
  };

  // Unsaved edits, for pull-to-refresh. The page keeps no dirty flag of its own
  // and its Save buttons are enabled at rest, so a form counts as dirty from
  // the first input inside it until it is submitted. Controls outside a form
  // (return month, public pricing) save as they change, so they never count.
  const dirtyFormsRef = useRef(new Set());
  const [refreshNotice, setRefreshNotice] = useState('');
  const markFormDirty = (e) => {
    if (e.target && e.target.form) dirtyFormsRef.current.add(e.target.form);
  };
  const markFormSaved = (e) => {
    dirtyFormsRef.current.delete(e.target);
  };

  // Refresh function for pull-to-refresh. A refetch would overwrite what is
  // typed, so it is skipped while any edit is unsaved.
  const handleRefresh = useCallback(async () => {
    const slugEdited = customSlugInput !== customSlug;
    if (dirtyFormsRef.current.size > 0 || tiersModified || slugEdited) {
      setRefreshNotice('Save or discard your changes before refreshing.');
      setTimeout(() => setRefreshNotice(''), 4000);
      return;
    }
    const isAdminRole = ['owner', 'admin'].includes(user?.role);
    await Promise.all([
      refreshUser(),
      loadMyFeatureRequests(),
      ...(isAdminRole ? [
        loadPricingSettings(),
        loadPricingTiers(),
        loadPaymentMethods(),
        loadMethodFees(),
        loadLeadSources(),
        loadExpenseCategories(),
        loadServiceAddons(),
        loadCompanyCode(),
        loadCompanyInfo(),
        loadPaymentConnections(),
        loadLeadSourceStats(leadSourceStatsPeriod),
      ] : []),
      user?.role === 'owner' ? loadBillingStatus() : null,
    ]);
  }, [refreshUser, user?.role, loadBillingStatus, loadPricingSettings, loadPricingTiers, loadPaymentMethods, loadMethodFees, loadLeadSources, loadExpenseCategories, loadServiceAddons, loadCompanyCode, loadCompanyInfo, loadPaymentConnections, loadLeadSourceStats, leadSourceStatsPeriod, loadMyFeatureRequests, tiersModified, customSlugInput, customSlug]);

  const { PullToRefreshIndicator } = usePullToRefresh(handleRefresh);

  useEffect(() => {
    if (user) {
      setProfile({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        phone: formatPhoneDisplay(user.phone) || '',
      });
      // Functional update: loadCompanyInfo fills the business contact fields
      // and this effect must not wipe them when the user object refreshes.
      setCompany((prev) => ({
        ...prev,
        name: user.companyName || '',
      }));
      // Load feature requests for all users
      loadMyFeatureRequests();
      // Load team members for all users
      loadTeamMembers();
      // Load pricing settings, payment methods, and lead sources for admins
      if (['owner', 'admin'].includes(user.role)) {
        loadPricingSettings();
        loadPricingTiers();
        loadPaymentMethods();
        loadMethodFees();
        loadLeadSources();
        loadExpenseCategories();
        loadServiceAddons();
        loadCompanyCode();
        loadCompanyInfo();
        loadPaymentConnections();
        loadLeadSourceStats();
        loadTeamInvitations();
      }
      // Owner only: GET /api/billing/status is open to any authenticated user,
      // but everything this section offers to do with it is owner-gated.
      if (user.role === 'owner') {
        loadBillingStatus();
      }
    }
  }, [user, loadBillingStatus, loadPricingSettings, loadPricingTiers, loadPaymentMethods, loadMethodFees, loadLeadSources, loadExpenseCategories, loadServiceAddons, loadCompanyCode, loadCompanyInfo, loadPaymentConnections, loadLeadSourceStats, loadMyFeatureRequests, loadTeamMembers, loadTeamInvitations]);

  // The return from Square's consent screen.
  //
  // The server redirects here with one of four outcome words and NOTHING ELSE
  // - no Square error text ever reaches the browser or the query string. The
  // query is then cleared with a replace so that a refresh does not repeat the
  // banner, and the Invoicing and Payments group is force-expanded so the owner lands
  // looking at the card rather than at a collapsed header.
  useEffect(() => {
    const outcome = new URLSearchParams(window.location.search).get('payments');
    if (!outcome) return;

    if (outcome === 'connected') {
      setPaymentsSuccess('Your Square account is connected.');
    } else if (outcome === 'location') {
      setPaymentsSuccess('Connected. Choose which Square location to invoice from.');
    } else if (outcome === 'nolocation') {
      setPaymentsError('Your Square account has no active location. Create a location in your Square dashboard, then reload this page.');
    } else {
      setPaymentsError('Square could not be connected. Please try again.');
    }

    setExpandedSections((prev) => ({ ...prev, payments: true }));
    navigate('/settings', { replace: true });
  }, [navigate]);

  // ?section=pricing|intake|account, from the Dashboard Getting started card
  // and the Help Contact tab. The
  // group it names (SECTION_LINKS) is expanded and scrolled to, and the query
  // cleared like the Square return above. The scroll waits for the target to
  // exist, and re-runs once the company code has loaded, because the signup
  // link card inside Customers waits for the code.
  const [scrollToSection, setScrollToSection] = useState(null);
  useEffect(() => {
    const section = new URLSearchParams(window.location.search).get('section');
    if (!Object.prototype.hasOwnProperty.call(SECTION_LINKS, section)) return;
    setExpandedSections((prev) => ({ ...prev, [SECTION_LINKS[section]]: true }));
    setScrollToSection(section);
    navigate('/settings', { replace: true });
  }, [navigate]);

  useEffect(() => {
    if (!scrollToSection) return;
    const el = document.getElementById(`settings-section-${scrollToSection}`);
    if (!el) return;
    if (el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setScrollToSection(null);
  }, [scrollToSection, companyCode]);

  // Start the OAuth round trip.
  //
  // THE JSON MODE IS USED RATHER THAN A PLAIN NAVIGATION because this API
  // authenticates by bearer header only - there is no cookie session, so a
  // top-level browser navigation to /api/payments/square/connect could never
  // be authenticated. axios sends the token, and the browser is moved only
  // once the authorize URL is in hand.
  //
  // Reconnect is the identical action: a fresh round trip replaces the row by
  // upsert.
  const handleConnectSquare = async () => {
    setPaymentsAction('connect');
    setPaymentsError('');
    setPaymentsSuccess('');
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/payments/square/connect?mode=json`
      );
      const authorizeUrl = response.data?.authorizeUrl;
      if (!authorizeUrl) {
        setPaymentsError('Square connections are not available right now.');
        setPaymentsAction(null);
        return;
      }
      window.location.href = authorizeUrl;
    } catch (err) {
      if (err.response?.status === 503) {
        setPaymentsError('Square connections are not available right now. Please try again later.');
      } else if (err.response?.data?.code === 'SUBSCRIPTION_LAPSED') {
        setPaymentsError(err.response.data.error);
      } else if (err.response?.data?.code === 'TRIAL_EXPIRED') {
        setPaymentsError(err.response.data.message);
      } else if (err.response?.status === 403) {
        setPaymentsError('Connecting a Square account requires the Pro plan.');
      } else {
        setPaymentsError('Could not start the Square connection. Please try again.');
      }
      setPaymentsAction(null);
    }
  };

  // Loaded only when the picker is actually rendered, so an ordinary connected
  // card costs no Square call on a Settings load.
  const loadSquareLocations = useCallback(async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/payments/square/locations`);
      setSquareLocations(response.data?.locations || []);
      setSquareLocationsLoaded(true);
    } catch (err) {
      setSquareLocations([]);
      setSquareLocationsLoaded(true);
      setPaymentsError('Could not read your Square locations. Please try again.');
    }
  }, []);

  const handleSaveSquareLocation = async () => {
    if (!selectedLocationId) return;
    setPaymentsAction('location');
    setPaymentsError('');
    setPaymentsSuccess('');
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/payments/square/location`,
        { locationId: selectedLocationId }
      );
      setPaymentConnection(response.data?.connection || null);
      setPaymentsSuccess('Square location saved.');
    } catch (err) {
      setPaymentsError(err.response?.data?.error || 'Could not save your Square location.');
    } finally {
      setPaymentsAction(null);
    }
  };

  const handleDisconnectSquare = async () => {
    setPaymentsAction('disconnect');
    setPaymentsError('');
    setPaymentsSuccess('');
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/payments/square/disconnect`, {});
      setShowDisconnectConfirm(false);
      setSquareLocations([]);
      setSquareLocationsLoaded(false);
      setSelectedLocationId('');
      setPaymentsSuccess('Your Square account has been disconnected.');
      await loadPaymentConnections();
    } catch (err) {
      setPaymentsError('Could not disconnect your Square account. Please try again.');
    } finally {
      setPaymentsAction(null);
    }
  };

  // The picker is populated the moment the card enters the "choose a location"
  // state, and only then.
  useEffect(() => {
    if (!paymentsEnabled) return;
    if (paymentConnection?.status !== 'connected') return;
    if (paymentConnection?.locationId) return;
    if (squareLocationsLoaded) return;
    loadSquareLocations();
  }, [paymentsEnabled, paymentConnection, squareLocationsLoaded, loadSquareLocations]);

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    // Format phone number as user types
    if (name === 'phone') {
      setProfile({ ...profile, phone: formatPhoneInput(value) });
    } else {
      setProfile({ ...profile, [name]: value });
    }
    setProfileSuccess('');
    setProfileError('');
  };

  const handlePasswordChange = (e) => {
    setPasswords({ ...passwords, [e.target.name]: e.target.value });
    setPasswordSuccess('');
    setPasswordError('');
  };

  const handleCompanyChange = (e) => {
    const { name, value } = e.target;
    setCompany({ ...company, [name]: name === 'businessPhone' ? formatPhoneInput(value) : value });
    setCompanySuccess('');
    setCompanyError('');
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setProfileLoading(true);
    setProfileError('');
    setProfileSuccess('');

    try {
      // Strip phone number formatting before sending to API
      const profileData = {
        ...profile,
        phone: stripPhone(profile.phone),
      };
      if (emailChanged) profileData.currentPassword = emailCurrentPassword;
      const response = await axios.put(`${process.env.REACT_APP_API_URL}/api/auth/profile`, profileData);
      // An email change revokes every earlier token, this one included. Keep
      // this device signed in on the fresh token the server returns.
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
      }
      setEmailCurrentPassword('');
      setProfileSuccess('Profile updated successfully');
      refreshUser();
    } catch (err) {
      setProfileError(err.response?.data?.error || 'Failed to update profile');
    } finally {
      setProfileLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordLoading(true);
    setPasswordError('');
    setPasswordSuccess('');

    if (passwords.newPassword !== passwords.confirmPassword) {
      setPasswordError('New passwords do not match');
      setPasswordLoading(false);
      return;
    }

    if (passwords.newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters');
      setPasswordLoading(false);
      return;
    }

    try {
      const response = await axios.put(`${process.env.REACT_APP_API_URL}/api/auth/password`, {
        currentPassword: passwords.currentPassword,
        newPassword: passwords.newPassword,
      });
      // The change revokes every earlier token, this one included. Keep this
      // device signed in on the fresh token the server returns.
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
      }
      setPasswordSuccess('Password changed successfully');
      setPasswords({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      setPasswordError(err.response?.data?.error || 'Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleCompanySubmit = async (e) => {
    e.preventDefault();
    setCompanyLoading(true);
    setCompanyError('');
    setCompanySuccess('');

    // Loose shape check only: something@something with no spaces. An odd but
    // plausible address is the owner's call, not ours. Empty clears the field.
    const businessEmail = company.businessEmail.trim();
    if (businessEmail && !/^[^\s@]+@[^\s@]+$/.test(businessEmail)) {
      setCompanyError('Business email does not look like an email address');
      setCompanyLoading(false);
      return;
    }

    try {
      // Empty strings are sent on purpose: the PUT keeps a field only when it
      // is absent or null, so '' is how the owner clears a number or address.
      await axios.put(`${process.env.REACT_APP_API_URL}/api/company`, {
        name: company.name,
        businessPhone: stripPhone(company.businessPhone),
        businessEmail,
        businessAddress: company.businessAddress.trim(),
        // Absent until GET /api/company has answered, so a save made before
        // then keeps the stored zone.
        ...(company.timezone ? { timezone: company.timezone } : {}),
      });
      setCompanySuccess('Company info updated successfully');
      // The zone lives on the profile too (user.timezone); refresh it so every
      // page reads the new one at once.
      refreshUser();
    } catch (err) {
      setCompanyError(err.response?.data?.error || 'Failed to update company info');
    } finally {
      setCompanyLoading(false);
    }
  };

  const handleInvoiceDefaultsChange = (e) => {
    const { name, value } = e.target;
    setInvoiceDefaults({ ...invoiceDefaults, [name]: value });
    setInvoiceDefaultsSuccess('');
    setInvoiceDefaultsError('');
  };

  const handleInvoiceDefaultsSubmit = async (e) => {
    e.preventDefault();
    setInvoiceDefaultsLoading(true);
    setInvoiceDefaultsError('');
    setInvoiceDefaultsSuccess('');

    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/api/company`, {
        defaultInvoiceTitle: invoiceDefaults.defaultInvoiceTitle,
        defaultInvoiceMessage: invoiceDefaults.defaultInvoiceMessage,
        autoInvoiceSquare: invoiceDefaults.autoInvoiceSquare,
      });
      setInvoiceDefaultsSuccess('Invoice defaults updated successfully');
    } catch (err) {
      setInvoiceDefaultsError(err.response?.data?.error || 'Failed to update invoice defaults');
    } finally {
      setInvoiceDefaultsLoading(false);
    }
  };

  // Pricing tiers handlers
  const handlePricingTierChange = (tierId, field, value) => {
    setPricingTiers(prev =>
      prev.map(t => t.id === tierId ? { ...t, [field]: value } : t)
    );
    setTiersModified(true);
    setPricingTiersSuccess('');
    setPricingTiersError('');
  };

  const handleAddTier = () => {
    const newTier = {
      id: `new_${Date.now()}`,
      name: '',
      default_price: '0.00',
      salt_price: '0.00',
      is_active: true,
      isNew: true
    };
    setPricingTiers(prev => [...prev, newTier]);
    setTiersModified(true);
    setPricingTiersSuccess('');
    setPricingTiersError('');
  };

  const handleDeleteTier = async (tier) => {
    // For new unsaved tiers, just remove from state
    if (tier.isNew) {
      setPricingTiers(prev => prev.filter(t => t.id !== tier.id));
      return;
    }

    const confirmMsg = `Are you sure you want to delete the "${tier.name}" tier?`;
    if (!window.confirm(confirmMsg)) return;

    try {
      const response = await axios.delete(`${process.env.REACT_APP_API_URL}/api/pricing-tiers/${tier.id}`);
      // The server reports a soft delete as soft_delete. Reading any other
      // field name lands in the hard-delete branch, which drops the tier out
      // of state entirely so it shows in neither list until a page reload.
      if (response.data.soft_delete) {
        // Tier was deactivated (in use). Flip the flag in place: both the
        // active list and the Inactive Tiers section are filtered views of
        // this one array, so the row moves across immediately and the
        // surviving active rows keep their sort order.
        setPricingTiers(prev =>
          prev.map(t => t.id === tier.id ? { ...t, is_active: false } : t)
        );
        setPricingTiersSuccess(`"${tier.name}" tier deactivated. Customers are still using it, so it was moved to Inactive Tiers.`);
      } else {
        // Tier was deleted
        setPricingTiers(prev => prev.filter(t => t.id !== tier.id));
        setPricingTiersSuccess(`"${tier.name}" tier deleted.`);
      }
    } catch (err) {
      setPricingTiersError(err.response?.data?.error || 'Failed to delete tier');
    }
  };

  const handleReactivateTier = async (tier) => {
    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/api/pricing-tiers/${tier.id}`, {
        ...tier,
        is_active: true
      });
      setPricingTiers(prev =>
        prev.map(t => t.id === tier.id ? { ...t, is_active: true } : t)
      );
      setPricingTiersSuccess(`"${tier.name}" tier reactivated`);
    } catch (err) {
      setPricingTiersError(err.response?.data?.error || 'Failed to reactivate tier');
    }
  };

  const handlePricingTiersSubmit = async (e) => {
    e.preventDefault();
    setPricingTiersLoading(true);
    setPricingTiersError('');
    setPricingTiersSuccess('');

    // Validate tiers
    const activeTiers = pricingTiers.filter(t => t.is_active);
    for (const tier of activeTiers) {
      if (!tier.name.trim()) {
        setPricingTiersError('All tiers must have a name');
        setPricingTiersLoading(false);
        return;
      }
      if (parseFloat(tier.default_price) < 0 || parseFloat(tier.salt_price) < 0) {
        setPricingTiersError('Prices cannot be negative');
        setPricingTiersLoading(false);
        return;
      }
    }

    try {
      // Separate new tiers from existing
      const newTiers = pricingTiers.filter(t => t.isNew);
      const existingTiers = pricingTiers.filter(t => !t.isNew);

      // Create new tiers
      for (const tier of newTiers) {
        await axios.post(`${process.env.REACT_APP_API_URL}/api/pricing-tiers`, {
          name: tier.name,
          // A brand new tier has no previous value to preserve, so a blank
          // field is simply no definition. The server stores that as NULL.
          description: tier.description ? tier.description.trim() : null,
          default_price: parseFloat(tier.default_price),
          salt_price: parseFloat(tier.salt_price),
          product_type: PRODUCT_TYPE
        });
      }

      // Bulk update existing tiers
      if (existingTiers.length > 0) {
        await axios.put(`${process.env.REACT_APP_API_URL}/api/pricing-tiers`, {
          tiers: existingTiers.map(t => ({
            id: t.id,
            name: t.name,
            // The server COALESCEs this column, so a tier sent without it keeps
            // whatever it had. Sending what the row holds is therefore the
            // difference between an edit that sticks and one that silently does
            // nothing, and it is why every tier is sent, not only edited ones.
            //
            // A string, including an EMPTY string, is sent as it stands; only a
            // tier that has never had a description sends null. That is what
            // lets an owner clear a definition: '' survives the COALESCE and
            // writes, where null would be read as "not sent" and keep the old
            // text forever.
            description: typeof t.description === 'string' ? t.description.trim() : null,
            default_price: parseFloat(t.default_price),
            salt_price: parseFloat(t.salt_price),
            is_active: t.is_active
          }))
        });
      }

      // Reload to get fresh data with proper IDs
      await loadPricingTiers();
      setPricingTiersSuccess('Pricing tiers saved successfully');
    } catch (err) {
      setPricingTiersError(err.response?.data?.error || 'Failed to save pricing tiers');
    } finally {
      setPricingTiersLoading(false);
    }
  };

  const handlePricingChange = (e) => {
    const { name, value } = e.target;
    setPricing({ ...pricing, [name]: value });
    setPricingSuccess('');
    setPricingError('');
  };

  const handlePricingSubmit = async (e) => {
    e.preventDefault();
    setPricingLoading(true);
    setPricingError('');
    setPricingSuccess('');

    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/api/settings/pricing`, pricing);
      setPricingSuccess('Pricing settings updated successfully');
    } catch (err) {
      setPricingError(err.response?.data?.error || 'Failed to update pricing settings');
    } finally {
      setPricingLoading(false);
    }
  };

  // Feature request handlers
  const handleFeatureRequestChange = (e) => {
    const { name, value } = e.target;
    setFeatureRequest({ ...featureRequest, [name]: value });
    setFeatureRequestSuccess('');
    setFeatureRequestError('');
  };

  const handleFeatureRequestSubmit = async (e) => {
    e.preventDefault();
    if (!featureRequest.subject.trim() || !featureRequest.description.trim()) {
      setFeatureRequestError('Please fill in both subject and description');
      return;
    }

    setFeatureRequestLoading(true);
    setFeatureRequestError('');
    setFeatureRequestSuccess('');

    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/feature-requests`, featureRequest);
      setFeatureRequestSuccess('Request submitted! Thanks for your feedback.');
      setFeatureRequest({ subject: '', description: '' });
      loadMyFeatureRequests(); // Reload the list
    } catch (err) {
      setFeatureRequestError(err.response?.data?.error || 'Failed to submit request');
    } finally {
      setFeatureRequestLoading(false);
    }
  };

  // Account deletion handler. The server performs the cascade; on success we
  // clear local credentials and leave for the landing page, because every
  // subsequent request from this session would 401 against a deleted user.
  const handleDeleteAccount = async () => {
    const companyName = (company.name || user?.companyName || '').trim();
    if (!companyName || deleteAccountConfirm.trim() !== companyName) {
      return;
    }

    setDeleteAccountLoading(true);
    setDeleteAccountError('');

    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/account`);
      // Drop the token and every cached preference before leaving.
      try {
        localStorage.clear();
      } catch (e) {
        console.error('Failed to clear local storage after account deletion:', e);
      }
      delete axios.defaults.headers.common['Authorization'];
      window.location.href = '/';
    } catch (err) {
      setDeleteAccountError(err.response?.data?.error || `Failed to delete account. Please try again or contact ${SUPPORT_EMAIL}.`);
      setDeleteAccountLoading(false);
    }
  };

  // Weather settings handlers
  const handleWeatherSettingsChange = (e) => {
    const { name, value, type } = e.target;
    setWeatherSettings(prev => ({
      ...prev,
      [name]: type === 'radio' ? value === 'true' : value
    }));
    setWeatherSettingsSuccess('');
    setWeatherSettingsError('');
  };

  const handleWeatherSettingsSubmit = async (e) => {
    e.preventDefault();

    // Validate if using home location
    if (weatherSettings.weatherUseHome && (!weatherSettings.weatherCity.trim() || !weatherSettings.weatherState)) {
      setWeatherSettingsError('Please enter a city and state for home location');
      return;
    }

    setWeatherSettingsLoading(true);
    setWeatherSettingsError('');
    setWeatherSettingsSuccess('');
    setTimezoneCheck(null);

    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/api/company`, {
        weatherCity: weatherSettings.weatherCity.trim() || null,
        weatherState: weatherSettings.weatherState || null,
        weatherUseHome: weatherSettings.weatherUseHome,
      });
      setWeatherSettingsSuccess('Weather settings saved!');
      // No suggestion for "use my current location": there is no city to check.
      const city = weatherSettings.weatherCity.trim();
      if (weatherSettings.weatherUseHome && city && weatherSettings.weatherState) {
        setTimezoneCheck({ city, state: weatherSettings.weatherState });
      }
    } catch (err) {
      setWeatherSettingsError(err.response?.data?.error || 'Failed to save weather settings');
    } finally {
      setWeatherSettingsLoading(false);
    }
  };

  // Season handlers
  const handleSeasonSettingsChange = (e) => {
    const { name, value } = e.target;
    setSeasonSettings(prev => ({ ...prev, [name]: Number(value) }));
    setSeasonSettingsSuccess('');
    setSeasonSettingsError('');
  };

  const handleSeasonSettingsSubmit = async (e) => {
    e.preventDefault();
    if (seasonSettings.startMonth === seasonSettings.endMonth) {
      setSeasonSettingsError('The season start and end month cannot be the same month');
      return;
    }
    setSeasonSettingsLoading(true);
    setSeasonSettingsError('');
    setSeasonSettingsSuccess('');
    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/api/company`, {
        seasonStartMonth: seasonSettings.startMonth,
        seasonEndMonth: seasonSettings.endMonth,
      });
      setSeasonSettingsSuccess('Season saved!');
    } catch (err) {
      setSeasonSettingsError(err.response?.data?.error || 'Failed to save season');
    } finally {
      setSeasonSettingsLoading(false);
    }
  };

  // Disclaimer settings handlers
  const handleDisclaimerSettingsChange = (e) => {
    const { name, value, type, checked } = e.target;
    setDisclaimerSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    setDisclaimerSettingsSuccess('');
    setDisclaimerSettingsError('');
  };

  // Saves on toggle: a lone checkbox with a separate Save button is easy to
  // flip and walk away from without saving.
  const handlePublicPricingToggle = async (e) => {
    const next = e.target.checked;
    setShowPublicPricing(next);
    setPublicPricingSaving(true);
    setPublicPricingMessage({ type: '', text: '' });
    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/api/company`, { showPublicPricing: next });
      setPublicPricingMessage({
        type: 'success',
        text: next
          ? 'Your prices are shown on your rates page'
          : 'Your rates page now says pricing is available on request',
      });
    } catch (err) {
      setShowPublicPricing(!next);
      setPublicPricingMessage({ type: 'error', text: err.response?.data?.error || 'Failed to save' });
    } finally {
      setPublicPricingSaving(false);
    }
  };

  const handleDisclaimerSettingsSubmit = async (e) => {
    e.preventDefault();
    setDisclaimerSettingsLoading(true);
    setDisclaimerSettingsError('');
    setDisclaimerSettingsSuccess('');

    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/api/company`, {
        intakeDisclaimerEnabled: disclaimerSettings.enabled,
        intakeDisclaimerText: disclaimerSettings.text,
        intakeSizeConfirmationText: disclaimerSettings.sizeConfirmationText,
      });
      setDisclaimerSettingsSuccess('Intake form settings saved!');
    } catch (err) {
      setDisclaimerSettingsError(err.response?.data?.error || 'Failed to save disclaimer settings');
    } finally {
      setDisclaimerSettingsLoading(false);
    }
  };

  // Fills the textarea for editing. Does not save: the owner still presses
  // Save. The button is only rendered while the field is empty, so this can
  // never overwrite anything the owner wrote.
  const handleUseStarterDisclaimer = () => {
    setDisclaimerSettings(prev => (
      prev.text.trim() ? prev : { ...prev, text: STARTER_DISCLAIMER }
    ));
    setDisclaimerSettingsSuccess('');
    setDisclaimerSettingsError('');
  };

  // Route optimization settings handlers
  const handleRouteSettingsChange = (e) => {
    const { name, checked } = e.target;
    setRouteSettings(prev => ({
      ...prev,
      [name]: checked
    }));
    setRouteSettingsSuccess('');
    setRouteSettingsError('');
  };

  const handleRouteSettingsSubmit = async (e) => {
    e.preventDefault();
    setRouteSettingsLoading(true);
    setRouteSettingsError('');
    setRouteSettingsSuccess('');

    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/api/company`, {
        routeOptimizeUseGps: routeSettings.useGps,
      });
      setRouteSettingsSuccess('Route settings saved!');
    } catch (err) {
      setRouteSettingsError(err.response?.data?.error || 'Failed to save route settings');
    } finally {
      setRouteSettingsLoading(false);
    }
  };

  // Custom slug handlers
  const validateSlug = (slug) => {
    if (!slug) return true; // Empty is valid (clears the slug)
    const slugRegex = /^[a-z0-9][a-z0-9-]{1,48}[a-z0-9]$/;
    return slugRegex.test(slug) && slug.length >= 3 && slug.length <= 50;
  };

  const handleSlugInputChange = (e) => {
    const value = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
    setCustomSlugInput(value);
    setSlugError('');
    setSlugSuccess('');
    setSlugAvailable(null);

    // Clear any pending timeout
    if (slugCheckTimeout) {
      clearTimeout(slugCheckTimeout);
    }

    // Debounced availability check
    if (value && value.length >= 3) {
      const timeout = setTimeout(async () => {
        if (!validateSlug(value)) {
          setSlugError('Must be 3-50 characters, start/end with letter or number');
          return;
        }
        // Don't check if it's the same as current slug
        if (value === customSlug) {
          setSlugAvailable(null);
          return;
        }
        setSlugChecking(true);
        try {
          const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/company/check-slug/${value}`);
          setSlugAvailable(response.data.available);
          if (!response.data.available) {
            setSlugError('This URL is already taken');
          }
        } catch (err) {
          console.error('Failed to check slug:', err);
        } finally {
          setSlugChecking(false);
        }
      }, 500);
      setSlugCheckTimeout(timeout);
    }
  };

  const handleSlugSave = async () => {
    setSlugError('');
    setSlugSuccess('');

    // Validate before saving
    if (customSlugInput && !validateSlug(customSlugInput)) {
      setSlugError('Must be 3-50 characters, start/end with letter or number, only lowercase letters, numbers, and hyphens');
      return;
    }

    setSlugSaving(true);
    try {
      const response = await axios.put(`${process.env.REACT_APP_API_URL}/api/company/slug`, {
        slug: customSlugInput || null
      });
      setCustomSlug(response.data.slug || '');
      setSlugSuccess(customSlugInput ? 'Custom URL saved!' : 'Custom URL cleared');
      setSlugAvailable(null);
    } catch (err) {
      setSlugError(err.response?.data?.error || 'Failed to save custom URL');
    } finally {
      setSlugSaving(false);
    }
  };

  const handleClearSlug = async () => {
    setCustomSlugInput('');
    setSlugError('');
    setSlugSuccess('');
    setSlugAvailable(null);

    if (customSlug) {
      // If there was a saved slug, clear it on the server
      setSlugSaving(true);
      try {
        await axios.put(`${process.env.REACT_APP_API_URL}/api/company/slug`, { slug: null });
        setCustomSlug('');
        setSlugSuccess('Custom URL cleared');
      } catch (err) {
        setSlugError(err.response?.data?.error || 'Failed to clear custom URL');
      } finally {
        setSlugSaving(false);
      }
    }
  };

  return (
    <div
      className="settings-page"
      onInputCapture={markFormDirty}
      onChangeCapture={markFormDirty}
      onSubmitCapture={markFormSaved}
    >
      <PullToRefreshIndicator />
      {refreshNotice && (
        <div className="notification-toast notification-info" role="status">{refreshNotice}</div>
      )}
      {/* Header */}
      <StaffHeader active="settings" />

      <div className="page-container">
        <div className="page-header">
          <h1 className="page-title">Settings</h1>
          <p className="text-muted">Manage your profile and account settings</p>
        </div>

        {/* Subscription ended: read-only access */}
        <LapsedBanner billing={billing} />

        {/* Account Banner */}
        <div className="account-banner">
          <div className="account-banner-name">
            <span className="account-icon">👤</span>
            {user?.firstName} {user?.lastName}
          </div>
          <div className="account-banner-details">
            <span className="account-detail">
              <span className="detail-label">Role:</span>
              <span className="detail-value">
                {user?.role === 'owner' && 'Owner'}
                {user?.role === 'admin' && 'Administrator'}
                {user?.role === 'crew_lead' && 'Crew Lead'}
                {user?.role === 'driver' && 'Driver'}
              </span>
            </span>
            <span className="account-divider">|</span>
            <span className="account-detail">
              <span className="detail-label">Company:</span>
              <span className="detail-value">{user?.companyName}</span>
            </span>
          </div>
        </div>

        {/* Settings groups, in this order: Company, Pricing, Customers, Texting
            and Email, Invoicing and Payments, Team, Account. A gate that differs
            from its group's sits on the card. The keys are the expandedSections
            keys (DEFAULT_SECTIONS). */}

        {/* 1. Company */}
        {isAdmin() && (
        <div className={`settings-section ${expandedSections.company ? 'expanded' : 'collapsed'}`}>
          <div className="settings-section-header" tabIndex={0} role="button" onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.currentTarget.click(); }}} onClick={() => toggleSection('company')}>
            <span className="section-arrow">{expandedSections.company ? '\u25BC' : '\u25B6'}</span>
            <h2 className="section-title">Company</h2>
          </div>
          <div className="settings-section-content">
              <div className="card mb-lg">
                <div className="card-header">
                  <h2 className="card-title">Company Information</h2>
              {!isOwner() && (
                <p className="text-muted" style={{ fontSize: '0.875rem', marginTop: '4px' }}>
                  As an admin, you can view and edit company settings
                </p>
              )}
            </div>

            <form onSubmit={handleCompanySubmit}>
              {companyError && <div className="alert alert-error">{companyError}</div>}
              {companySuccess && <div className="alert alert-success">{companySuccess}</div>}

              <div className="form-group">
                <label className="form-label" htmlFor="companyName">Company Name</label>
                <input
                  type="text"
                  id="companyName"
                  name="name"
                  className="form-input"
                  value={company.name}
                  onChange={handleCompanyChange}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="businessPhone">Business Phone</label>
                <input
                  type="tel"
                  id="businessPhone"
                  name="businessPhone"
                  className="form-input"
                  value={company.businessPhone}
                  onChange={handleCompanyChange}
                  placeholder="555.555.5555"
                  autoComplete="tel"
                />
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="businessEmail">Business Email</label>
                <input
                  type="text"
                  inputMode="email"
                  id="businessEmail"
                  name="businessEmail"
                  className="form-input"
                  value={company.businessEmail}
                  onChange={handleCompanyChange}
                  maxLength={255}
                  autoComplete="email"
                />
                <p className="text-muted" style={{ fontSize: '0.875rem', marginTop: '4px' }}>
                  Shown to your customers on the portal's Contact Us card.
                </p>
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="businessAddress">Business Address</label>
                <textarea
                  id="businessAddress"
                  name="businessAddress"
                  className="form-input"
                  rows={3}
                  value={company.businessAddress}
                  onChange={handleCompanyChange}
                  maxLength={500}
                  autoComplete="street-address"
                  placeholder={'123 Main St\nAppleton, WI 54911'}
                />
                <p className="text-muted" style={{ fontSize: '0.875rem', marginTop: '4px' }}>
                  Printed at the top of customer statements.
                </p>
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="companyTimezone">Time zone</label>
                <TimezoneSelect
                  id="companyTimezone"
                  value={company.timezone}
                  options={timezoneOptions}
                  onChange={handleCompanyChange}
                />
                <p className="text-muted" style={{ fontSize: '0.875rem', marginTop: '4px' }}>
                  Today's date, texting quiet hours (8:00 AM to 9:00 PM), your season, reports and every time shown in the app follow this zone.
                </p>
              </div>

              <button type="submit" className="btn btn-primary" disabled={companyLoading}>
                {companyLoading ? 'Saving...' : 'Save Company Info'}
              </button>
            </form>
              </div>

              <div className="card mb-lg">
                <div className="card-header">
                  <h2 className="card-title">Season</h2>
                  <p className="text-muted" style={{ fontSize: '0.875rem', marginTop: '4px' }}>
                    Your season runs from the 1st of the start month to the last day of the end month. It sets "This season" on the Dashboard, Reports, statements and Archive season.
                  </p>
                </div>

                <form onSubmit={handleSeasonSettingsSubmit}>
                  {seasonSettingsError && <div className="alert alert-error">{seasonSettingsError}</div>}
                  {seasonSettingsSuccess && <div className="alert alert-success">{seasonSettingsSuccess}</div>}

                  <div className="form-row" style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
                    <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                      <label className="form-label" htmlFor="seasonStartMonth">Starts</label>
                      <select
                        id="seasonStartMonth"
                        name="startMonth"
                        className="form-select"
                        value={seasonSettings.startMonth}
                        onChange={handleSeasonSettingsChange}
                      >
                        {SEASON_MONTHS.map((label, i) => (
                          <option key={label} value={i + 1}>{label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                      <label className="form-label" htmlFor="seasonEndMonth">Ends</label>
                      <select
                        id="seasonEndMonth"
                        name="endMonth"
                        className="form-select"
                        value={seasonSettings.endMonth}
                        onChange={handleSeasonSettingsChange}
                      >
                        {SEASON_MONTHS.map((label, i) => (
                          <option key={label} value={i + 1}>{label}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <button type="submit" className="btn btn-primary" disabled={seasonSettingsLoading}>
                    {seasonSettingsLoading ? 'Saving...' : 'Save Season'}
                  </button>
                </form>
              </div>

              <div className="card mb-lg">
                <div className="card-header">
                  <h2 className="card-title">Weather Location</h2>
                  <p className="text-muted" style={{ fontSize: '0.875rem', marginTop: '4px' }}>
                    Where the Dashboard weather forecast is for.
                  </p>
                </div>

                <form onSubmit={handleWeatherSettingsSubmit}>
                  {weatherSettingsError && <div className="alert alert-error">{weatherSettingsError}</div>}
                  {weatherSettingsSuccess && <div className="alert alert-success">{weatherSettingsSuccess}</div>}

                  <div className="form-group">
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="weatherUseHome"
                        value="false"
                        checked={!weatherSettings.weatherUseHome}
                        onChange={handleWeatherSettingsChange}
                      />
                      <span className="radio-text">Use the location of the phone or computer you are on</span>
                    </label>
                  </div>

                  <div className="form-group">
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="weatherUseHome"
                        value="true"
                        checked={weatherSettings.weatherUseHome}
                        onChange={handleWeatherSettingsChange}
                      />
                      <span className="radio-text">Use home location:</span>
                    </label>
                  </div>

                  {weatherSettings.weatherUseHome && (
                    <div className="form-row" style={{ display: 'flex', gap: '12px', marginTop: '12px', marginBottom: '16px' }}>
                      <div className="form-group" style={{ flex: 2, marginBottom: 0 }}>
                        <label className="form-label" htmlFor="weatherCity">City</label>
                        <input
                          type="text"
                          id="weatherCity"
                          name="weatherCity"
                          className="form-input"
                          value={weatherSettings.weatherCity}
                          onChange={handleWeatherSettingsChange}
                          placeholder="e.g., Appleton"
                          maxLength={100}
                        />
                      </div>
                      <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                        <label className="form-label" htmlFor="weatherState">State</label>
                        <select
                          id="weatherState"
                          name="weatherState"
                          className="form-select"
                          value={weatherSettings.weatherState}
                          onChange={handleWeatherSettingsChange}
                        >
                          <option value="">Select</option>
                          {US_STATES.map(state => (
                            <option key={state} value={state}>{state}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}

                  <button type="submit" className="btn btn-primary" disabled={weatherSettingsLoading}>
                    {weatherSettingsLoading ? 'Saving...' : 'Save Weather Settings'}
                  </button>
                </form>

                <TimezoneSuggestion
                  check={timezoneCheck}
                  companyZone={timezone || company.timezone}
                  zoneOptions={timezoneOptions}
                  refreshUser={refreshUser}
                  onApplied={(zone) => setCompany((prev) => ({ ...prev, timezone: zone }))}
                />
              </div>

              <div className="card mb-lg">
                <div className="card-header">
                  <h2 className="card-title">Route Optimization</h2>
                  <p className="text-muted" style={{ fontSize: '0.875rem', marginTop: '4px' }}>
                    Where Storm Mode starts when it puts a route in order.
                  </p>
                </div>

                <form onSubmit={handleRouteSettingsSubmit}>
                  {routeSettingsError && <div className="alert alert-error" aria-live="polite">{routeSettingsError}</div>}
                  {routeSettingsSuccess && <div className="alert alert-success" aria-live="polite">{routeSettingsSuccess}</div>}

                  <div className="form-group">
                    <label className="checkbox-label" style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        name="useGps"
                        checked={routeSettings.useGps}
                        onChange={handleRouteSettingsChange}
                        style={{ marginTop: '3px' }}
                      />
                      <div>
                        <span style={{ fontWeight: 500 }}>Start route optimization from the driver's current location</span>
                        <p className="text-muted" style={{ fontSize: '0.875rem', marginTop: '4px', marginBottom: 0 }}>
                          When on, the driver's phone location is the starting point. When off, the
                          route's starting address is used, or the last completed stop once one is done.
                        </p>
                      </div>
                    </label>
                  </div>

                  <button type="submit" className="btn btn-primary" disabled={routeSettingsLoading}>
                    {routeSettingsLoading ? 'Saving...' : 'Save Route Settings'}
                  </button>
                </form>
              </div>
          </div>
        </div>
        )}

        {/* 2. Pricing. The id is the ?section=pricing deep link target. */}
        {isAdmin() && (
        <div id="settings-section-pricing" className={`settings-section ${expandedSections.pricing ? 'expanded' : 'collapsed'}`}>
          <div className="settings-section-header" tabIndex={0} role="button" onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.currentTarget.click(); }}} onClick={() => toggleSection('pricing')}>
            <span className="section-arrow">{expandedSections.pricing ? '\u25BC' : '\u25B6'}</span>
            <h2 className="section-title">Pricing</h2>
          </div>
          <div className="settings-section-content">
              <div className="card mb-lg">
                <div className="card-header">
                  <h2 className="card-title">Pricing Tiers</h2>
                  <p className="text-muted" style={{ fontSize: '0.875rem', marginTop: '4px' }}>
                    Your driveway sizes and what each one costs. Customers pick a size on your signup form.
                  </p>
                  {/* Rendered once, above the list, not on every row. What each
                      size means is shown to customers on your signup form and
                      beside the tier on the customer record, so it is worth
                      saying in your own words. A size left blank simply shows
                      no definition. */}
                  <p className="text-muted" style={{ fontSize: '0.8125rem', marginTop: '8px' }}>
                    {SIZE_FRAMING}
                  </p>
                  {PRODUCT_TYPE === 'snow' && (
                    <p className="text-muted" style={{ fontSize: '0.8125rem', marginTop: '4px' }}>
                      Flat price (used when depth pricing is off, and for routes created before).
                    </p>
                  )}
                </div>

                <form onSubmit={handlePricingTiersSubmit}>
                  {pricingTiersError && <div className="alert alert-error">{pricingTiersError}</div>}
                  {pricingTiersSuccess && <div className="alert alert-success">{pricingTiersSuccess}</div>}

                  <div className="pricing-tiers-list">
                    {pricingTiers.filter(t => t.is_active).map((tier, index) => (
                      <div key={tier.id} className="pricing-tier-item">
                        <div className="tier-row">
                          <div className="tier-field tier-field-name">
                            <label className="form-label">Tier Name</label>
                            <input
                              type="text"
                              className="form-input"
                              value={tier.name}
                              onChange={(e) => handlePricingTierChange(tier.id, 'name', e.target.value)}
                              placeholder="e.g., Small, Medium, Large"
                            />
                          </div>
                          <div className="tier-field tier-field-name">
                            <label className="form-label">What this size means</label>
                            <input
                              type="text"
                              className="form-input"
                              value={tier.description || ''}
                              onChange={(e) => handlePricingTierChange(tier.id, 'description', e.target.value)}
                              placeholder="e.g., 3 to 4 cars"
                            />
                          </div>
                          <div className="tier-field tier-field-price">
                            <label className="form-label" title="Used when depth pricing is off, and for routes created before">
                              {PRODUCT_TYPE === 'snow' ? 'Flat price' : 'Base Price'}
                            </label>
                            <div className="input-with-prefix compact">
                              <span className="input-prefix">$</span>
                              <input
                                type="number"
                                className="form-input"
                                value={tier.default_price}
                                onChange={(e) => handlePricingTierChange(tier.id, 'default_price', e.target.value)}
                                step="0.01"
                                min="0"
                              />
                            </div>
                          </div>
                          <div className="tier-field tier-field-price">
                            <label className="form-label">Salt Price</label>
                            <div className="input-with-prefix compact">
                              <span className="input-prefix">$</span>
                              <input
                                type="number"
                                className="form-input"
                                value={tier.salt_price}
                                onChange={(e) => handlePricingTierChange(tier.id, 'salt_price', e.target.value)}
                                step="0.01"
                                min="0"
                              />
                            </div>
                          </div>
                          <div className="tier-actions">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleDeleteTier(tier)}
                              title="Delete tier"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Inactive tiers */}
                  {pricingTiers.filter(t => !t.is_active).length > 0 && (
                    <div className="inactive-tiers-section">
                      <h4 className="inactive-tiers-title">Inactive Tiers</h4>
                      <p className="text-muted" style={{ fontSize: '0.8rem', marginBottom: '12px' }}>
                        These tiers are deactivated but still referenced by existing customers.
                      </p>
                      {pricingTiers.filter(t => !t.is_active).map((tier) => (
                        <div key={tier.id} className="inactive-tier-item">
                          <span className="inactive-tier-name">{tier.name} - Flat: ${parseFloat(tier.default_price).toFixed(2)}, Salt: ${parseFloat(tier.salt_price).toFixed(2)}</span>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline"
                            onClick={() => handleReactivateTier(tier)}
                          >
                            Reactivate
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="pricing-tiers-actions">
                    <button type="button" className="btn btn-outline" onClick={handleAddTier}>
                      + Add Tier
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={pricingTiersLoading || !tiersModified}
                    >
                      {pricingTiersLoading ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </form>
              </div>

              {PRODUCT_TYPE === 'snow' && (
                <DepthPricingCard tiersVersion={pricingTiersVersion} onTiersChanged={loadPricingTiers} />
              )}

              {/* Service Add-ons Card */}
              <div className="card mb-lg">
                <div className="card-header">
                  <h2 className="card-title">Service Add-ons</h2>
                </div>

            <form onSubmit={handleServiceAddonsSubmit}>
              {serviceAddonsError && <div className="alert alert-error">{serviceAddonsError}</div>}
              {serviceAddonsSuccess && <div className="alert alert-success">{serviceAddonsSuccess}</div>}

              <p className="text-muted mb-md">Configure add-on services that can be added to stops. These appear when completing a stop.</p>
              <p className="text-muted mb-md addon-help">
                A blank tier cell uses the Default $. A percent add-on is that percent of the stop's snow + salt price, so its Default $ and tier cells are not used.
                Untick "Show on rates page and signup" to keep an add-on off your public pages. Untick "On-call premium applies" to leave it out of the on-call premium, which is added only on routes priced by depth.
                "Ask for a detail" (for example "Done by") lets you record a detail per customer, shown on the customer card and the driver's stop card.
              </p>

              <div className="addon-tier-grid">
                {/* Header row */}
                <div className="addon-tier-header">
                  <div className="addon-col-toggle"></div>
                  <div className="addon-col-name">Add-on Name</div>
                  <div className="addon-col-price">Default $</div>
                  {pricingTiers.filter(t => t.is_active !== false).map(tier => (
                    <div key={tier.id} className="addon-col-tier">{tier.name}</div>
                  ))}
                  <div className="addon-col-actions"></div>
                </div>

                {/* Active add-on rows */}
                {serviceAddons.filter(a => a.enabled).map((addon) => {
                  const isPercent = addon.fees?.pricingType === 'percent';
                  return (
                  <React.Fragment key={addon.id}>
                  <div className="addon-tier-row addon-tier-row-main">
                    <div className="addon-col-toggle">
                      <input
                        type="checkbox"
                        checked={addon.enabled}
                        onChange={() => handleServiceAddonToggle(addon.id)}
                      />
                    </div>
                    <div className="addon-col-name">
                      <input
                        type="text"
                        className="form-input"
                        value={addon.name}
                        onChange={(e) => handleAddonNameChange(addon.id, e.target.value)}
                        placeholder="Add-on name"
                      />
                    </div>
                    <div className="addon-col-price">
                      <div className="addon-price-input-wrapper">
                        <span className="price-prefix">$</span>
                        <input
                          type="number"
                          className="form-input addon-price-input"
                          value={addon.price}
                          onChange={(e) => handleAddonPriceChange(addon.id, e.target.value)}
                          step="0.01"
                          min="0"
                          placeholder="0.00"
                          disabled={isPercent}
                        />
                      </div>
                    </div>
                    {pricingTiers.filter(t => t.is_active !== false).map(tier => (
                      <div key={tier.id} className="addon-col-tier">
                        <div className="addon-price-input-wrapper">
                          <span className="price-prefix">$</span>
                          <input
                            type="number"
                            className="form-input addon-price-input"
                            value={addonTierPrices[addon.id]?.[tier.id] ?? ''}
                            onChange={(e) => handleAddonTierPriceChange(addon.id, tier.id, e.target.value)}
                            step="0.01"
                            min="0"
                            placeholder={isPercent ? '' : addon.price.toFixed(2)}
                            disabled={isPercent}
                          />
                        </div>
                      </div>
                    ))}
                    <div className="addon-col-actions">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleDeleteServiceAddon(addon.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                  <div className="addon-tier-options-row">
                    <AddonFeeOptions
                      fees={addon.fees}
                      idPrefix={`addon-${addon.id}`}
                      onChange={(patch) => handleAddonFeesChange(addon.id, patch)}
                    />
                  </div>
                  </React.Fragment>
                  );
                })}

                {/* Add new row */}
                <div className="addon-tier-row addon-tier-row-new">
                  <div className="addon-col-toggle"></div>
                  <div className="addon-col-name">
                    <input
                      type="text"
                      className="form-input"
                      placeholder="New add-on name"
                      value={newAddonName}
                      onChange={(e) => setNewAddonName(e.target.value)}
                    />
                  </div>
                  <div className="addon-col-price">
                    <div className="addon-price-input-wrapper">
                      <span className="price-prefix">$</span>
                      <input
                        type="number"
                        className="form-input addon-price-input"
                        placeholder="0.00"
                        value={newAddonPrice}
                        onChange={(e) => setNewAddonPrice(e.target.value)}
                        step="0.01"
                        min="0"
                        disabled={newAddonFees.pricingType === 'percent'}
                      />
                    </div>
                  </div>
                  {pricingTiers.filter(t => t.is_active !== false).map(tier => (
                    <div key={tier.id} className="addon-col-tier">
                      <div className="addon-price-input-wrapper">
                        <span className="price-prefix">$</span>
                        <input
                          type="number"
                          className="form-input addon-price-input"
                          value={addonTierPrices['new']?.[tier.id] ?? ''}
                          onChange={(e) => handleAddonTierPriceChange('new', tier.id, e.target.value)}
                          step="0.01"
                          min="0"
                          placeholder="0.00"
                          disabled={newAddonFees.pricingType === 'percent'}
                        />
                      </div>
                    </div>
                  ))}
                  <div className="addon-col-actions">
                    <button type="button" className="btn btn-outline" onClick={handleAddServiceAddon}>
                      Add
                    </button>
                  </div>
                </div>
                <div className="addon-tier-options-row addon-tier-options-row-new">
                  <AddonFeeOptions
                    fees={newAddonFees}
                    idPrefix="addon-new"
                    onChange={(patch) => handleAddonFeesChange('new', patch)}
                  />
                </div>
              </div>

              {/* Inactive add-ons section */}
              {serviceAddons.filter(a => !a.enabled).length > 0 && (
                <div className="inactive-addons-section">
                  <p className="inactive-label">Inactive Add-ons</p>
                  {serviceAddons.filter(a => !a.enabled).map((addon) => (
                    <div key={addon.id} className="service-addon-item inactive">
                      <span style={{ flex: 1 }}>{addon.name}</span>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline"
                        onClick={() => handleServiceAddonToggle(addon.id)}
                      >
                        Reactivate
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <button type="submit" className="btn btn-primary mt-md" disabled={serviceAddonsLoading}>
                {serviceAddonsLoading ? 'Saving...' : 'Save Add-ons'}
              </button>
            </form>
              </div>

              <div className="card mb-lg">
                <div className="card-header">
                  <h2 className="card-title">Public Rates Page</h2>
                  <p className="text-muted" style={{ fontSize: '0.875rem', marginTop: '4px' }}>
                    A page anyone can open that lists your snow removal prices, with a Sign up button to your intake form. Your intake form links to it as "See our rates".
                  </p>
                </div>
                {publicPricingMessage.text && (
                  <div className={`alert alert-${publicPricingMessage.type === 'error' ? 'error' : 'success'}`} aria-live="polite">{publicPricingMessage.text}</div>
                )}
                <div className="form-group">
                  <label className="checkbox-label" style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={showPublicPricing}
                      onChange={handlePublicPricingToggle}
                      disabled={publicPricingSaving}
                      style={{ marginTop: '3px' }}
                    />
                    <div>
                      <span style={{ fontWeight: 500 }}>Show my prices on my public rates page</span>
                      <p className="text-muted" style={{ fontSize: '0.875rem', marginTop: '4px', marginBottom: 0 }}>
                        When off, the page says pricing is available on request and shows only the Sign up button.
                      </p>
                    </div>
                  </label>
                </div>
                {(customSlug || companyCode) && (
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label" htmlFor="ratesPageUrl">Rates page link</label>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      <input
                        id="ratesPageUrl"
                        type="text"
                        className="form-input"
                        style={{ flex: '1 1 220px', minWidth: 0 }}
                        readOnly
                        value={`${window.location.origin}/rates/${customSlug || companyCode}`}
                      />
                      <a
                        className="btn btn-outline"
                        href={`/rates/${customSlug || companyCode}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Open
                      </a>
                    </div>
                  </div>
                )}
              </div>

              <div className="card mb-lg">
                <div className="card-header">
                  <h2 className="card-title">Referral credit (shown in Storm Mode)</h2>
                  <p className="text-muted" style={{ fontSize: '0.875rem', marginTop: '4px' }}>
                    Shows drivers how much credit a customer has earned for referrals in the same storm. It is for reference only: it is not taken off the price or off Square invoices. To give the credit, enter it as a discount when you complete the stop.
                  </p>
                </div>

                <form onSubmit={handlePricingSubmit}>
                  {pricingError && <div className="alert alert-error">{pricingError}</div>}
                  {pricingSuccess && <div className="alert alert-success">{pricingSuccess}</div>}

                  <div className="pricing-section">
                    <div className="grid grid-2">
                      <div className="form-group">
                        <label className="form-label" htmlFor="referral_discount">Credit per referral</label>
                        <div className="input-with-prefix">
                          <span className="input-prefix">$</span>
                          <input
                            type="number"
                            id="referral_discount"
                            name="referral_discount"
                            className="form-input"
                            value={pricing.referral_discount}
                            onChange={handlePricingChange}
                            step="0.01"
                            min="0"
                          />
                        </div>
                        <small className="text-muted">Shown for each referred customer serviced in the same storm (routes with the same Storm ID).</small>
                      </div>
                      <div className="form-group">
                        <label className="form-label" htmlFor="minimum_price">Lowest price after referral credit</label>
                        <div className="input-with-prefix">
                          <span className="input-prefix">$</span>
                          <input
                            type="number"
                            id="minimum_price"
                            name="minimum_price"
                            className="form-input"
                            value={pricing.minimum_price}
                            onChange={handlePricingChange}
                            step="0.01"
                            min="0"
                          />
                        </div>
                        <small className="text-muted">When the referral credit is shown, the price shown never goes below this. It does not set a minimum price for any other stop.</small>
                      </div>
                    </div>
                  </div>

                  <button type="submit" className="btn btn-primary" disabled={pricingLoading}>
                    {pricingLoading ? 'Saving...' : 'Save Pricing Settings'}
                  </button>
                </form>
              </div>
          </div>
        </div>
        )}

        {/* 3. Customers. The id is the ?section=intake deep link target.
            The signup link and the disclaimer wait for the company code. */}
        {isAdmin() && (
        <div id="settings-section-intake" className={`settings-section ${expandedSections.customers ? 'expanded' : 'collapsed'}`}>
          <div className="settings-section-header" tabIndex={0} role="button" onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.currentTarget.click(); }}} onClick={() => toggleSection('customers')}>
            <span className="section-arrow">{expandedSections.customers ? '\u25BC' : '\u25B6'}</span>
            <h2 className="section-title">Customers</h2>
          </div>
          <div className="settings-section-content">
              {companyCode && (
              <>
              <div className="card mb-lg">
                <div className="card-header">
                  <h2 className="card-title">Signup link</h2>
                </div>

                <p className="text-muted mb-md">
                  Share this link with potential customers to let them sign up directly.
                  New submissions will appear in your Customers list with a "New - From Form" badge.
                </p>

                {/* Primary Signup Link */}
                <div className="intake-link-section">
                  <label className="form-label">Your Signup Link:</label>
                  <div className="intake-link-row">
                    <input
                      type="text"
                      className="form-input intake-link-input"
                      value={`${window.location.origin}/new-customer/${customSlug || companyCode}`}
                      readOnly
                      onClick={(e) => e.target.select()}
                    />
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => {
                        navigator.clipboard.writeText(`${window.location.origin}/new-customer/${customSlug || companyCode}`);
                        setCopiedLink(true);
                        // Ticks "Share your signup link" on the Dashboard card.
                        axios.post(`${process.env.REACT_APP_API_URL}/api/dashboard/onboarding/intake-link-copied`)
                          .catch((err) => console.error('Failed to record intake link copy:', err));
                        setTimeout(() => setCopiedLink(false), 2000);
                      }}
                    >
                      {copiedLink ? 'Copied!' : 'Copy Link'}
                    </button>
                  </div>
                  {customSlug && (
                    <small className="text-muted">
                      Original link still works: {window.location.origin}/new-customer/{companyCode}
                    </small>
                  )}
                </div>

                {/* Custom URL Section */}
                <div className="custom-slug-section">
                  <label className="form-label">Custom URL (optional)</label>
                  <p className="text-muted slug-description">
                    A short, memorable ending for your links. It changes both your signup link and your rates page link. The original links keep working.
                  </p>

                  {slugError && <div className="alert alert-error slug-alert">{slugError}</div>}
                  {slugSuccess && <div className="alert alert-success slug-alert">{slugSuccess}</div>}

                  <div className="slug-input-row">
                    <span className="slug-prefix">{window.location.origin}/new-customer/</span>
                    <div className="slug-input-wrapper">
                      <input
                        type="text"
                        className={`form-input slug-input ${slugAvailable === true ? 'input-success' : ''} ${slugAvailable === false ? 'input-error' : ''}`}
                        value={customSlugInput}
                        onChange={handleSlugInputChange}
                        placeholder="your-company-name"
                        maxLength={50}
                      />
                      {slugChecking && <span className="slug-status checking">Checking...</span>}
                      {!slugChecking && slugAvailable === true && <span className="slug-status available">Available</span>}
                      {!slugChecking && slugAvailable === false && <span className="slug-status taken">Taken</span>}
                    </div>
                  </div>

                  <div className="slug-actions">
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleSlugSave}
                      disabled={slugSaving || slugChecking || slugAvailable === false || (customSlugInput === customSlug)}
                    >
                      {slugSaving ? 'Saving...' : 'Save Custom URL'}
                    </button>
                    {(customSlugInput || customSlug) && (
                      <button
                        type="button"
                        className="btn btn-outline"
                        onClick={handleClearSlug}
                        disabled={slugSaving}
                      >
                        Clear
                      </button>
                    )}
                  </div>

                  <small className="text-muted slug-help">
                    Must be 3-50 characters. Only lowercase letters, numbers, and hyphens allowed.
                  </small>
                </div>
              </div>

              <div className="card mb-lg">
                <div className="card-header">
                  <h2 className="card-title">Intake Form Disclaimer</h2>
                  <p className="text-muted" style={{ fontSize: '0.875rem', marginTop: '4px' }}>
                    Configure the payment disclaimer and the driveway size confirmation line shown on the customer intake form
                  </p>
                </div>

                <form onSubmit={handleDisclaimerSettingsSubmit}>
                  {disclaimerSettingsError && <div className="alert alert-error" aria-live="polite">{disclaimerSettingsError}</div>}
                  {disclaimerSettingsSuccess && <div className="alert alert-success" aria-live="polite">{disclaimerSettingsSuccess}</div>}

                  <div className="form-group">
                    <label className="checkbox-label" style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        name="enabled"
                        checked={disclaimerSettings.enabled}
                        onChange={handleDisclaimerSettingsChange}
                        style={{ marginTop: '3px' }}
                      />
                      <div>
                        <span style={{ fontWeight: 500 }}>Show disclaimer on intake form</span>
                        <p className="text-muted" style={{ fontSize: '0.875rem', marginTop: '4px', marginBottom: 0 }}>
                          When enabled, customers must accept the disclaimer before submitting the intake form
                        </p>
                      </div>
                    </label>
                  </div>

                  {disclaimerSettings.enabled && (
                    <>
                      <div className="form-group">
                        <label className="form-label" htmlFor="disclaimerText">Disclaimer Text</label>
                        <p style={{ fontSize: '0.875rem', marginTop: 0, marginBottom: '4px' }}>
                          Payment terms your customers agree to when they sign up. Leave this blank if you do not want to show any. Use <code>{'{company_name}'}</code> and your business name will be filled in automatically.
                        </p>
                        <p className="text-muted" style={{ fontSize: '0.8125rem', marginTop: 0, marginBottom: '8px' }}>
                          If you want to include lien or collection language, check what your state requires first.
                        </p>
                        <textarea
                          id="disclaimerText"
                          name="text"
                          className="form-textarea"
                          value={disclaimerSettings.text}
                          onChange={handleDisclaimerSettingsChange}
                          rows={5}
                          placeholder="Enter your payment terms, or leave blank to show none"
                        />
                        {!disclaimerSettings.text.trim() && (
                          <button
                            type="button"
                            className="btn btn-outline"
                            style={{ marginTop: '8px' }}
                            onClick={handleUseStarterDisclaimer}
                          >
                            Use a basic starter
                          </button>
                        )}
                      </div>

                      {/* Same test the public form applies (IntakeForm.js), so
                          an empty field previews as what customers actually get. */}
                      {disclaimerSettings.text ? (
                        <div className="disclaimer-preview">
                          <label className="form-label">Preview</label>
                          <div className="disclaimer-preview-box">
                            {disclaimerSettings.text.replace(/{company_name}/g, company.name || 'Your Company')}
                          </div>
                        </div>
                      ) : (
                        <p className="text-muted" style={{ fontSize: '0.875rem' }}>
                          No payment terms will be shown on your signup form.
                        </p>
                      )}
                    </>
                  )}

                  {/* Outside the enabled check on purpose. This line belongs to
                      the driveway size question, not to the payment
                      disclaimer, so turning the disclaimer off must not take it
                      down with it. It is a promise about how you handle a size
                      that turns out to be wrong, shown under the size options,
                      so it is yours to word. */}
                  <div className="form-group">
                    <label className="form-label" htmlFor="sizeConfirmationText">Driveway Size Confirmation Line</label>
                    <textarea
                      id="sizeConfirmationText"
                      name="sizeConfirmationText"
                      className="form-textarea"
                      value={disclaimerSettings.sizeConfirmationText}
                      onChange={handleDisclaimerSettingsChange}
                      rows={3}
                      placeholder={DEFAULT_SIZE_CONFIRMATION}
                    />
                    <small className="text-muted">
                      Shown under the driveway size question on your signup form. Leave it as it stands to use the default wording.
                    </small>
                  </div>

                  <div className="disclaimer-actions">
                    <button type="submit" className="btn btn-primary" disabled={disclaimerSettingsLoading}>
                      {disclaimerSettingsLoading ? 'Saving...' : 'Save Intake Form Settings'}
                    </button>
                  </div>
                </form>
              </div>
              </>
              )}

              <div className="card mb-lg">
                <div className="card-header">
                  <h2 className="card-title">Lead Sources</h2>
                </div>

                <form onSubmit={handleLeadSourcesSubmit}>
                  {leadSourcesError && <div className="alert alert-error">{leadSourcesError}</div>}
                  {leadSourcesSuccess && <div className="alert alert-success">{leadSourcesSuccess}</div>}

                  <p className="text-muted mb-md">Configure "How did you find me?" options for tracking customer acquisition.</p>

                  <div className="payment-methods-list">
                    {leadSources.map((source) => (
                      <div key={source.id} className="payment-method-item">
                        <label className="payment-method-toggle">
                          <input
                            type="checkbox"
                            checked={source.enabled}
                            onChange={() => handleLeadSourceToggle(source.id)}
                          />
                          <span className="payment-method-name">{source.name}</span>
                          {source.isBuiltIn && <span className="built-in-badge">Built-in</span>}
                        </label>
                        {!source.isBuiltIn && (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDeleteLeadSource(source.id)}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    ))}
                  </div>

                  <div className="add-payment-method">
                    <input
                      type="text"
                      className="form-input"
                      placeholder="New lead source name"
                      value={newSourceName}
                      onChange={(e) => setNewSourceName(e.target.value)}
                    />
                    <button type="button" className="btn btn-outline" onClick={handleAddLeadSource}>
                      Add Source
                    </button>
                  </div>

                  <button type="submit" className="btn btn-primary mt-md" disabled={leadSourcesLoading}>
                    {leadSourcesLoading ? 'Saving...' : 'Save Lead Sources'}
                  </button>
                </form>

                {/* Lead Source Stats */}
                <div className="lead-source-stats mt-lg">
                  <div className="stats-header">
                    <h3>Lead Source Analytics</h3>
                    <select
                      className="form-select stats-period-select"
                      value={leadSourceStatsPeriod}
                      onChange={(e) => {
                        setLeadSourceStatsPeriod(e.target.value);
                        loadLeadSourceStats(e.target.value);
                      }}
                    >
                      <option value="all">All Time</option>
                      <option value="season">This Season</option>
                      <option value="month">This Month</option>
                    </select>
                  </div>

                  {leadSourceStatsLoading ? (
                    <div className="stats-loading">Loading...</div>
                  ) : leadSourceStats.stats.length === 0 ? (
                    <div className="stats-empty">No lead source data yet</div>
                  ) : (
                    <div className="stats-list">
                      {leadSourceStats.stats.map((stat) => (
                        <div key={stat.id} className="stats-item">
                          <div className="stats-item-info">
                            <span className="stats-item-name">{stat.name}</span>
                            <span className="stats-item-count">{stat.count} ({stat.percentage}%)</span>
                          </div>
                          <div className="stats-bar-container">
                            <div
                              className="stats-bar"
                              style={{ width: `${stat.percentage}%` }}
                            />
                          </div>
                        </div>
                      ))}
                      <div className="stats-total">
                        Total: {leadSourceStats.total} customers with lead source
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* What a new signup receives when you confirm their size
                  (migration 093). Self-contained: it loads and saves its
                  own settings. */}
              <WelcomeMessageCard />

              {/* On-call service requests (migration 102). */}
              {expandedSections.customers && <ServiceRequestsSetting />}
          </div>
        </div>
        )}

        {/* 4. Texting and Email. The group shows for every owner and admin,
            because Season Check-in also sends by email; only the texting
            cards are gated. */}
        {isAdmin() && (
        <div className={`settings-section ${expandedSections.messaging ? 'expanded' : 'collapsed'}`}>
          <div className="settings-section-header" tabIndex={0} role="button" onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.currentTarget.click(); }}} onClick={() => toggleSection('messaging')}>
            <span className="section-arrow">{expandedSections.messaging ? '\u25BC' : '\u25B6'}</span>
            <h2 className="section-title">Texting and Email</h2>
          </div>
          <div className="settings-section-content">
              {/* Text Messaging cards (SMS Phase 2). Owner and admin, Pro and up; a
                  lapsed company still sees them read-only, as the server allows its
                  GETs. They load once this group is opened. */}
              {smsEnabled && (hasFeature(effectiveTier, 'sms') || isLapsed()) && (
                <SmsSettingsSection expanded={expandedSections.messaging} isOwner={isOwner()} />
              )}

              {/* What Remind on the Dashboard season tile sends to customers
                  who have not answered (migration 098). Self-contained. */}
              <SeasonCheckinCard />
          </div>
        </div>
        )}

        {/* 5. Invoicing and Payments. Square Account comes first because
            auto-send in Invoice Defaults depends on it. */}
        {isAdmin() && (
        <div className={`settings-section ${expandedSections.payments ? 'expanded' : 'collapsed'}`}>
          <div className="settings-section-header" tabIndex={0} role="button" onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.currentTarget.click(); }}} onClick={() => toggleSection('payments')}>
            <span className="section-arrow">{expandedSections.payments ? '\u25BC' : '\u25B6'}</span>
            <h2 className="section-title">Invoicing and Payments</h2>
          </div>
          <div className="settings-section-content">
        {/* Payments Section.
            isAdmin() is requireAdmin's client counterpart, so owner and admin
            see this and crew_lead and driver do not, matching the server
            exactly. paymentsEnabled mirrors the server-side switch; the server
            enforces it independently on all six endpoints. */}
              {paymentsEnabled && (
              <div className="card mb-lg">
                <div className="card-header">
                  <h2 className="card-title">Square Account</h2>
                  <p className="text-muted" style={{ fontSize: '0.875rem', marginTop: '4px' }}>
                    Which Square account your customer invoices are sent from
                  </p>
                </div>

                {paymentsError && <div className="alert alert-error">{paymentsError}</div>}
                {paymentsSuccess && <div className="alert alert-success">{paymentsSuccess}</div>}

                {paymentsLoading && <p className="text-muted">Loading...</p>}

                {/* 1. NOT CONNECTED */}
                {!paymentsLoading && (!paymentConnection || paymentConnection.status === 'disconnected') && (
                  <div>
                    <p style={{ fontWeight: 500, marginBottom: '4px' }}>Connect your Square account</p>
                    <p className="text-muted" style={{ fontSize: '0.875rem' }}>
                      Invoices will be sent from your own Square account, and your customers' payments
                      land in your own Square balance.
                    </p>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleConnectSquare}
                      disabled={paymentsAction === 'connect'}
                    >
                      {paymentsAction === 'connect' ? 'Opening Square...' : 'Connect Square'}
                    </button>
                  </div>
                )}

                {/* 3. NEEDS ATTENTION. Disconnect stays available so an owner
                       can abandon a broken connection. */}
                {!paymentsLoading && paymentConnection?.status === 'error' && (
                  <div>
                    <p style={{ fontWeight: 500, marginBottom: '4px' }}>Square connection needs attention</p>
                    <p className="text-muted" style={{ fontSize: '0.875rem' }}>
                      Your Square connection needs attention. Reconnect to keep sending invoices from
                      your own Square account.
                    </p>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleConnectSquare}
                      disabled={paymentsAction === 'connect'}
                      style={{ marginRight: '0.5rem' }}
                    >
                      {paymentsAction === 'connect' ? 'Opening Square...' : 'Reconnect Square'}
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setShowDisconnectConfirm(true)}
                      disabled={paymentsAction === 'disconnect'}
                    >
                      Disconnect
                    </button>
                  </div>
                )}

                {/* 2. CONNECTED */}
                {!paymentsLoading && paymentConnection?.status === 'connected' && paymentConnection.locationId && (
                  <div>
                    <p style={{ fontWeight: 500, marginBottom: '4px' }}>
                      Connected to {paymentConnection.accountName || 'your Square account'}
                      {/* The badge renders ONLY for sandbox. A production badge
                          on every card is noise; a sandbox badge is a warning
                          that these invoices are not real. */}
                      {paymentConnection.environment === 'sandbox' && (
                        <span className="text-muted" style={{ fontSize: '0.75rem', marginLeft: '0.5rem' }}>
                          SANDBOX
                        </span>
                      )}
                    </p>
                    <p className="text-muted" style={{ fontSize: '0.875rem', marginBottom: '4px' }}>
                      Location: {paymentConnection.locationName || paymentConnection.locationId}
                    </p>
                    {formatBillingDate(paymentConnection.connectedAt, timezone) && (
                      <p className="text-muted" style={{ fontSize: '0.875rem' }}>
                        Connected {formatBillingDate(paymentConnection.connectedAt, timezone)}
                      </p>
                    )}
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setShowDisconnectConfirm(true)}
                      disabled={paymentsAction === 'disconnect'}
                    >
                      Disconnect
                    </button>
                  </div>
                )}

                {/* 4. CHOOSE A LOCATION */}
                {!paymentsLoading && paymentConnection?.status === 'connected' && !paymentConnection.locationId
                  && squareLocations.length > 0 && (
                  <div>
                    <p style={{ fontWeight: 500, marginBottom: '4px' }}>Choose a Square location</p>
                    <p className="text-muted" style={{ fontSize: '0.875rem' }}>
                      Your Square account has more than one location. Invoices will be created under
                      the one you pick.
                    </p>
                    <div className="form-group">
                      <select
                        className="form-input"
                        value={selectedLocationId}
                        onChange={(e) => setSelectedLocationId(e.target.value)}
                      >
                        <option value="">Select a location</option>
                        {squareLocations.map((loc) => (
                          <option key={loc.id} value={loc.id}>{loc.name}</option>
                        ))}
                      </select>
                    </div>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleSaveSquareLocation}
                      disabled={!selectedLocationId || paymentsAction === 'location'}
                    >
                      {paymentsAction === 'location' ? 'Saving...' : 'Save location'}
                    </button>
                  </div>
                )}

                {/* 4b. ZERO ACTIVE LOCATIONS. Without this branch the owner
                        gets an empty dropdown and a Save button that cannot
                        succeed, and invoicing stays blocked with a message
                        telling them to do the impossible. */}
                {!paymentsLoading && paymentConnection?.status === 'connected' && !paymentConnection.locationId
                  && squareLocationsLoaded && squareLocations.length === 0 && (
                  <div>
                    <p style={{ fontWeight: 500, marginBottom: '4px' }}>Your Square account has no active location.</p>
                    <p className="text-muted" style={{ fontSize: '0.875rem' }}>
                      Create a location in your Square dashboard, then reload this page.
                    </p>
                  </div>
                )}
              </div>
              )}

              <div className="card mb-lg">
                <div className="card-header">
                  <h2 className="card-title">Invoice Defaults</h2>
                  <p className="text-muted" style={{ fontSize: '0.875rem', marginTop: '4px' }}>
                    The title and message on the Square invoices SnowTrack sends for you. A route's own invoice title and message, if you set them on the route, are used instead.
                  </p>
                </div>

                <form onSubmit={handleInvoiceDefaultsSubmit}>
                  {invoiceDefaultsError && <div className="alert alert-error">{invoiceDefaultsError}</div>}
                  {invoiceDefaultsSuccess && <div className="alert alert-success">{invoiceDefaultsSuccess}</div>}

                  <div className="form-group">
                    <label className="form-label" htmlFor="defaultInvoiceTitle">Default Invoice Title</label>
                    <input
                      type="text"
                      id="defaultInvoiceTitle"
                      name="defaultInvoiceTitle"
                      className="form-input"
                      value={invoiceDefaults.defaultInvoiceTitle}
                      onChange={handleInvoiceDefaultsChange}
                      placeholder="{route_name}"
                    />
                    <small className="text-muted">Leave blank to use route name</small>
                  </div>

                  <div className="form-group">
                    <label className="form-label" htmlFor="defaultInvoiceMessage">Default Invoice Message</label>
                    <textarea
                      id="defaultInvoiceMessage"
                      name="defaultInvoiceMessage"
                      className="form-textarea"
                      value={invoiceDefaults.defaultInvoiceMessage}
                      onChange={handleInvoiceDefaultsChange}
                      rows={3}
                      placeholder="Thank you for your business!"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Available Variables</label>
                    <div className="text-muted" style={{ fontSize: '0.875rem' }}>
                      <code>{'{route_name}'}</code> - Route name (e.g., "North Side")<br />
                      <code>{'{service_date}'}</code> - Date of service<br />
                      <code>{'{customer_name}'}</code> - Customer's full name
                    </div>
                  </div>

                  <div className="form-group" style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid var(--color-border)' }}>
                    <label className="checkbox-label" style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={invoiceDefaults.autoInvoiceSquare}
                        onChange={(e) => setInvoiceDefaults(prev => ({ ...prev, autoInvoiceSquare: e.target.checked }))}
                        style={{ marginTop: '3px' }}
                      />
                      <div>
                        <span style={{ fontWeight: 500 }}>Send the Square invoice automatically when a stop is completed</span>
                        <p className="text-muted" style={{ fontSize: '0.875rem', marginTop: '4px', marginBottom: 0 }}>
                          When a driver completes a stop with Square as the payment, SnowTrack emails that customer a Square invoice for the stop. The customer needs an email address on file. This needs the Pro plan and a connected Square account (Square Account, above). If either is missing, nothing is sent and Storm Mode tells you why.
                        </p>
                        <p className="text-muted" style={{ fontSize: '0.875rem', marginTop: '8px', marginBottom: 0 }}>
                          <strong>On routes priced by snow depth:</strong> a stop finished before you enter the storm depth has no price yet, so its invoice waits. As soon as you save the depth, every Square stop that was waiting is invoiced. Stops finished after the depth is entered are invoiced straight away. If you change the depth after an invoice has gone out, that invoice is not changed or sent again. Bill All lists it so you can bill the difference, or void it and send a new one.
                        </p>
                      </div>
                    </label>
                  </div>

                  <button type="submit" className="btn btn-primary" disabled={invoiceDefaultsLoading}>
                    {invoiceDefaultsLoading ? 'Saving...' : 'Save Invoice Defaults'}
                  </button>
                </form>
              </div>

              <div className="card mb-lg">
                <div className="card-header">
                  <h2 className="card-title">Payment methods and fees</h2>
                  <p style={{ color: 'var(--color-text-light)', fontSize: '0.875rem', margin: '4px 0 0' }}>Turn on the ways customers can pay you. Only the ones turned on show in Storm Mode and on the customer form. The fee for each method is what you expect to lose to processing; it is used on the route Print Summary to show what the route netted.</p>
                </div>

                <form onSubmit={handlePaymentMethodsSubmit}>
                  {paymentMethodsError && <div className="alert alert-error">{paymentMethodsError}</div>}
                  {paymentMethodsSuccess && <div className="alert alert-success">{paymentMethodsSuccess}</div>}

                  <div className="payment-methods-list">
                    {visibleMethods(paymentMethods).map((method) => {
                      const fee = feeFor(methodFees, method.id);
                      return (
                        <div key={method.id} className="payment-method-item">
                          <label className="payment-method-toggle">
                            <input
                              type="checkbox"
                              checked={method.enabled}
                              onChange={() => handlePaymentMethodToggle(method.id)}
                            />
                            <span className="payment-method-name">{method.name}</span>
                            {method.isBuiltIn && <span className="built-in-badge">Built-in</span>}
                          </label>
                          <div className="payment-method-fee">
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              aria-label={`${method.name} fee percent`}
                              value={fee.fee_percent}
                              onChange={(e) => handleMethodFeeChange(method.id, 'fee_percent', e.target.value)}
                              disabled={!method.enabled}
                            />
                            <span>% +</span>
                            <span>$</span>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              aria-label={`${method.name} flat fee`}
                              value={fee.fee_flat}
                              onChange={(e) => handleMethodFeeChange(method.id, 'fee_flat', e.target.value)}
                              disabled={!method.enabled}
                            />
                            {!method.isBuiltIn && (
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => handleDeletePaymentMethod(method.id)}
                              >
                                Delete
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <p className="text-muted mb-md">Square's fee here is also what SnowTrack records in Expenses when a Square payment arrives. With Square turned off, Expenses uses Square's standard invoice rate.</p>

                  <div className="add-payment-method">
                    <input
                      type="text"
                      className="form-input"
                      placeholder="New payment method name"
                      maxLength={50}
                      value={newMethodName}
                      onChange={(e) => setNewMethodName(e.target.value)}
                    />
                    <button type="button" className="btn btn-outline" onClick={handleAddPaymentMethod}>
                      Add Method
                    </button>
                  </div>

                  <button type="submit" className="btn btn-primary mt-md" disabled={paymentMethodsLoading}>
                    {paymentMethodsLoading ? 'Saving...' : 'Save Payment Methods and Fees'}
                  </button>
                </form>
              </div>

              <div className="card mb-lg">
                <div className="card-header">
                  <h2 className="card-title">Expense Categories</h2>
                </div>

                <form onSubmit={handleExpenseCategoriesSubmit}>
                  {expenseCategoriesError && <div className="alert alert-error">{expenseCategoriesError}</div>}
                  {expenseCategoriesSuccess && <div className="alert alert-success">{expenseCategoriesSuccess}</div>}

                  <p className="text-muted mb-md">Configure expense categories for tracking business costs. Disable categories you don't use.</p>

                  <div className="payment-methods-list">
                    {expenseCategories.map((category) => (
                      <div key={category.id} className="payment-method-item">
                        <label className="payment-method-toggle">
                          <input
                            type="checkbox"
                            checked={category.enabled}
                            onChange={() => handleExpenseCategoryToggle(category.id)}
                          />
                          <span className="payment-method-name">{category.name}</span>
                          {category.isBuiltIn && <span className="built-in-badge">Built-in</span>}
                        </label>
                        {!category.isBuiltIn && (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDeleteExpenseCategory(category.id)}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    ))}
                  </div>

                  <div className="add-payment-method">
                    <input
                      type="text"
                      className="form-input"
                      placeholder="New category name"
                      value={newCategoryName}
                      onChange={(e) => setNewCategoryName(e.target.value)}
                    />
                    <button type="button" className="btn btn-outline" onClick={handleAddExpenseCategory}>
                      Add Category
                    </button>
                  </div>

                  <button type="submit" className="btn btn-primary mt-md" disabled={expenseCategoriesLoading}>
                    {expenseCategoriesLoading ? 'Saving...' : 'Save Categories'}
                  </button>
                </form>
              </div>
          </div>
        </div>
        )}

        {/* 6. Team */}
        {isAdmin() && (
        <div className={`settings-section ${expandedSections.team ? 'expanded' : 'collapsed'}`}>
          <div className="settings-section-header" tabIndex={0} role="button" onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.currentTarget.click(); }}} onClick={() => toggleSection('team')}>
            <span className="section-arrow">{expandedSections.team ? '\u25BC' : '\u25B6'}</span>
            <h2 className="section-title">Team</h2>
          </div>
          <div className="settings-section-content">
            <div className="card mb-lg">
              <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h2 className="card-title">Team Members</h2>
                  <p className="text-muted" style={{ fontSize: '0.875rem', marginTop: '4px' }}>
                    Manage your team and send invitations
                  </p>
                  {/* Admins cannot open Your Subscription (owner only), so the
                      plan is shown here, read-only. effectiveTier, so a lapsed
                      company reads Free. */}
                  {user?.role === 'admin' && (
                    <p className="text-muted" style={{ fontSize: '0.875rem', marginTop: '4px' }}>
                      Plan: {getPlanName(effectiveTier)}
                      {user?.subscriptionStatus === 'trial' && ` (trial: ${user?.stormEventsUsed || 0} of 1 free storm used)`}
                    </p>
                  )}
                </div>
                {isAdmin() && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => setShowInviteModal(true)}
                  >
                    + Invite Team Member
                  </button>
                )}
              </div>

              {teamError && <div className="alert alert-error">{teamError}</div>}
              {teamSuccess && <div className="alert alert-success">{teamSuccess}</div>}

              {teamLoading ? (
                <div className="loading-container" style={{ padding: '24px', textAlign: 'center' }}>
                  <div className="loading-spinner"></div>
                </div>
              ) : (
                <div className="team-list">
                  {teamMembers.map(member => (
                    <div key={member.id} className="team-member-row">
                      <div className="team-member-info">
                        <div className="team-member-name">
                          {member.first_name} {member.last_name}
                          {member.id === user?.id && <span className="you-badge">(You)</span>}
                        </div>
                        <div className="team-member-email">{member.email}</div>
                      </div>
                      <div className="team-member-actions">
                        {editingMember === member.id ? (
                          <select
                            className="form-input role-select"
                            value={member.role}
                            onChange={(e) => handleUpdateRole(member.id, e.target.value)}
                            onBlur={() => setEditingMember(null)}
                            autoFocus
                          >
                            {isOwner() && <option value="admin">Admin</option>}
                            <option value="crew_lead">Crew Lead</option>
                            <option value="driver">Driver</option>
                          </select>
                        ) : (
                          <span className={`badge ${getRoleBadgeClass(member.role)}`}>
                            {formatRole(member.role)}
                          </span>
                        )}
                        {isAdmin() && member.role !== 'owner' && member.id !== user?.id && (
                          <>
                            <button
                              type="button"
                              className="btn btn-secondary btn-sm"
                              onClick={() => setEditingMember(member.id)}
                              title="Change role"
                            >
                              Edit
                            </button>
                            {confirmRemove === member.id ? (
                              <div className="confirm-remove">
                                <span>Remove?</span>
                                <button
                                  type="button"
                                  className="btn btn-error btn-sm"
                                  onClick={() => handleRemoveMember(member.id)}
                                >
                                  Yes
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-secondary btn-sm"
                                  onClick={() => setConfirmRemove(null)}
                                >
                                  No
                                </button>
                              </div>
                            ) : (
                              <button
                                type="button"
                                className="btn btn-error btn-sm"
                                onClick={() => setConfirmRemove(member.id)}
                                title="Remove from team"
                              >
                                Remove
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Pending Invitations */}
              {isAdmin() && teamInvitations.length > 0 && (
                <div className="pending-invitations">
                  <h3 className="subsection-title">Pending Invitations</h3>
                  {teamInvitations.map(invite => (
                    <div key={invite.id} className="invitation-row">
                      <div className="invitation-info">
                        <div className="invitation-email">{invite.email}</div>
                        <div className="invitation-meta">
                          <span className={`badge ${getRoleBadgeClass(invite.role)}`}>
                            {formatRole(invite.role)}
                          </span>
                          <span className="invitation-expires">
                            Expires {new Date(invite.expires_at).toLocaleDateString(undefined, inZone(timezone))}
                          </span>
                        </div>
                      </div>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => handleCancelInvitation(invite.id)}
                      >
                        Cancel
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        )}

        {/* Invite Modal */}
        {isAdmin() && showInviteModal && (
          <div className="modal-overlay" onClick={() => setShowInviteModal(false)}>
            <div className="modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
              <div className="modal-header">
                <h2>Invite Team Member</h2>
                <button className="modal-close" onClick={() => setShowInviteModal(false)} aria-label="Close">&times;</button>
              </div>
              <form onSubmit={handleSendInvite}>
                {inviteError && (
                  <div className="alert alert-error" aria-live="polite">
                    {inviteError}
                    {inviteErrorIsLimit && (
                      <>
                        {' '}
                        <a href="/pricing" target="_blank" rel="noopener noreferrer">See plans</a>
                        , or deactivate a team member you no longer need to free a seat.
                      </>
                    )}
                  </div>
                )}

                <div className="form-group">
                  <label className="form-label" htmlFor="inviteEmail">Email Address</label>
                  <input
                    type="email"
                    id="inviteEmail"
                    className="form-input"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="teammate@example.com"
                    required
                    autoFocus
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="inviteRole">Role</label>
                  <select
                    id="inviteRole"
                    className="form-input"
                    value={inviteRole}
                    onChange={(e) => setInviteRole(e.target.value)}
                  >
                    {isOwner() && <option value="admin">Admin - Full access, can manage team</option>}
                    <option value="crew_lead">Crew Lead - Can manage routes</option>
                    <option value="driver">Driver - Can view assigned routes only</option>
                  </select>
                </div>

                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowInviteModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={inviteLoading}>
                    {inviteLoading ? 'Sending...' : 'Send Invitation'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* 7. Account. Everyone; the subscription and Delete Account cards
            are owner only. */}
        <div id="settings-section-account" className={`settings-section ${expandedSections.account ? 'expanded' : 'collapsed'}`}>
          <div className="settings-section-header" tabIndex={0} role="button" onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.currentTarget.click(); }}} onClick={() => toggleSection('account')}>
            <span className="section-arrow">{expandedSections.account ? '\u25BC' : '\u25B6'}</span>
            <h2 className="section-title">Account</h2>
          </div>
          <div className="settings-section-content">
            <div className="card mb-lg">
              <div className="card-header">
                <h2 className="card-title">Profile Information</h2>
              </div>

          <form onSubmit={handleProfileSubmit}>
            {profileError && <div className="alert alert-error">{profileError}</div>}
            {profileSuccess && <div className="alert alert-success">{profileSuccess}</div>}

            <div className="grid grid-2">
              <div className="form-group">
                <label className="form-label" htmlFor="firstName">First Name</label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  className="form-input"
                  value={profile.firstName}
                  onChange={handleProfileChange}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="lastName">Last Name</label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  className="form-input"
                  value={profile.lastName}
                  onChange={handleProfileChange}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                className="form-input"
                value={profile.email}
                onChange={handleProfileChange}
                required
              />
            </div>

            {emailChanged && (
              <div className="form-group">
                <label className="form-label" htmlFor="emailCurrentPassword">Current password</label>
                <input
                  type="password"
                  id="emailCurrentPassword"
                  name="emailCurrentPassword"
                  className="form-input"
                  value={emailCurrentPassword}
                  onChange={(e) => { setEmailCurrentPassword(e.target.value); setProfileSuccess(''); setProfileError(''); }}
                  autoComplete="current-password"
                  required
                />
                <p className="text-muted" style={{ fontSize: '0.875rem', marginTop: '4px' }}>Needed to change your email address.</p>
              </div>
            )}

            <div className="form-group">
              <label className="form-label" htmlFor="phone">Phone</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                className="form-input"
                value={profile.phone}
                onChange={handleProfileChange}
                placeholder="(555) 123-4567"
              />
            </div>

            <button type="submit" className="btn btn-primary" disabled={profileLoading}>
              {profileLoading ? 'Saving...' : 'Save Profile'}
            </button>
          </form>
        </div>

        {/* Product email (migration 111), owners only */}
        {isOwner() && <ProductEmailCard user={user} timezone={timezone} onSaved={() => refreshUser({ silent: true })} />}

        {/* Password Section */}
        <div className="card mb-lg">
          <div className="card-header">
            <h2 className="card-title">Change Password</h2>
          </div>

          <form onSubmit={handlePasswordSubmit}>
            {passwordError && <div className="alert alert-error">{passwordError}</div>}
            {passwordSuccess && <div className="alert alert-success">{passwordSuccess}</div>}

            <div className="form-group">
              <label className="form-label" htmlFor="currentPassword">Current Password</label>
              <input
                type="password"
                id="currentPassword"
                name="currentPassword"
                className="form-input"
                value={passwords.currentPassword}
                onChange={handlePasswordChange}
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="newPassword">New Password</label>
              <input
                type="password"
                id="newPassword"
                name="newPassword"
                className="form-input"
                value={passwords.newPassword}
                onChange={handlePasswordChange}
                placeholder="At least 8 characters"
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="confirmPassword">Confirm New Password</label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                className="form-input"
                value={passwords.confirmPassword}
                onChange={handlePasswordChange}
                required
              />
            </div>

            <button type="submit" className="btn btn-primary" disabled={passwordLoading}>
              {passwordLoading ? 'Changing...' : 'Change Password'}
            </button>
          </form>
        </div>

        {/* SnowTrack subscription. Owner only - POST /api/billing/portal and
            POST /api/billing/cancel are both requireOwner, and the portal can
            cancel the plan, so showing this to an admin would only produce
            403s. Plan changes deliberately go through Stripe's portal rather
            than a custom UI here: it handles proration, currency and tax
            correctly and we do not have to. */}
              {isOwner() && (
              <div className="card mb-lg">
                <div className="card-header">
                  <h2 className="card-title">Your SnowTrack&trade; Subscription</h2>
                </div>

                {billingLoading && !billing && (
                  <p className="text-muted">Loading subscription details...</p>
                )}

                {billingError && (
                  <div className="alert alert-error" aria-live="polite">{billingError}</div>
                )}

                {billing && (() => {
                  const status = String(billing.status || 'none').toLowerCase();
                  // Pre-formatted on the server as the calendar day in the
                  // company's zone. Not formatBillingDate: that renders in the
                  // browser zone, which need not be the company's.
                  const periodEnd = billing.currentPeriodEndDisplay || null;
                  const isBillable = BILLABLE_STATUSES.includes(status);
                  const canCancel = ['active', 'trialing'].includes(status) && !billing.cancelAtPeriodEnd;

                  return (
                    <>
                      <div className="billing-summary">
                        <div className="billing-row">
                          <span className="billing-label">Plan</span>
                          <span className="billing-value">{getPlanName(billing.tier)}</span>
                        </div>
                        <div className="billing-row">
                          <span className="billing-label">Status</span>
                          <span className={`billing-status billing-status-${status}`}>
                            {/* Same condition as the pending-cancellation line below,
                                so the badge and the line always agree. */}
                            {billing.cancelAtPeriodEnd && periodEnd
                              ? `Ends ${periodEnd}`
                              : (BILLING_STATUS_LABELS[status] || status)}
                          </span>
                        </div>

                        {/* Plan usage. Shown before a refused create tells the
                            owner the hard way, and it names the over-limit case
                            explicitly rather than leaving them to read
                            "60 of 25" and work it out. Counts and limits both
                            come from the server, which is what enforces them. */}
                        {billing.limits && billing.usage && (
                          <>
                            <div className="billing-row">
                              <span className="billing-label">Users</span>
                              <span className={`billing-value${isOverLimit(billing, 'users') ? ' billing-value-over' : ''}`}>
                                {formatUsage(billing.usage.users, billing.limits.users)}
                                {isOverLimit(billing, 'users') && ' (over your plan limit)'}
                              </span>
                            </div>
                            <div className="billing-row">
                              <span className="billing-label">Customers</span>
                              <span className={`billing-value${isOverLimit(billing, 'customers') ? ' billing-value-over' : ''}`}>
                                {formatUsage(billing.usage.customers, billing.limits.customers)}
                                {isOverLimit(billing, 'customers') && ' (over your plan limit)'}
                              </span>
                            </div>
                          </>
                        )}
                      </div>

                      {billing.limits && billing.usage
                        && (isOverLimit(billing, 'users') || isOverLimit(billing, 'customers')) && (
                        <div className="alert alert-warning">
                          You are over a plan limit. <strong>Nothing has been removed or hidden</strong>,
                          and routes, Storm Mode, invoicing and reports all keep working. You cannot add
                          another customer or invite another user until you upgrade or free a slot.
                          Archiving a customer frees a slot.
                        </div>
                      )}

                      {billing.cancelAtPeriodEnd && periodEnd && (
                        <>
                          <div className="alert alert-warning">
                            Your plan ends on <strong>{periodEnd}</strong>. You have full
                            access until then. After that your account becomes read-only: you
                            can still view your customers, history and reports, and you can
                            resubscribe any time to run routes again.
                          </div>
                          <p className="text-muted" style={{ fontSize: '0.9rem' }}>
                            Changed your mind? Use Manage Billing to keep your plan.
                          </p>
                        </>
                      )}

                      {(billing.cancelAtPeriodEnd || ENDED_STATUSES.includes(status)) && (
                        <div className="form-group" style={{ maxWidth: '320px' }}>
                          <label className="form-label" htmlFor="returnMonth">Expected return month</label>
                          <select
                            id="returnMonth"
                            className="form-select"
                            value={billing.returnMonth || NOT_SURE}
                            onChange={(e) => handleReturnMonthChange(e.target.value)}
                            disabled={returnMonthSaving}
                          >
                            <option value={NOT_SURE}>Not sure</option>
                            {/* A saved month that has dropped out of the 12-month
                                window stays listed, so the select still shows it. */}
                            {billing.returnMonth
                              && !returnMonthOptions().some((o) => o.value === billing.returnMonth) && (
                              <option value={billing.returnMonth}>{formatReturnMonth(billing.returnMonth)}</option>
                            )}
                            {returnMonthOptions().map((o) => (
                              <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                          </select>
                          <p className="text-muted" style={{ fontSize: '0.85rem', marginTop: '6px', marginBottom: 0 }}>
                            We will email you once, about two weeks before.
                          </p>
                          {returnMonthError && (
                            <div className="alert alert-error" aria-live="polite">{returnMonthError}</div>
                          )}
                        </div>
                      )}

                      {!billing.cancelAtPeriodEnd && status === 'past_due' && (
                        <div className="alert alert-error">
                          <strong>Payment failed.</strong> Please update your payment method to
                          keep your plan. Your access continues while Stripe retries the charge.
                        </div>
                      )}

                      {!billing.cancelAtPeriodEnd && ['active', 'trialing'].includes(status) && periodEnd && (
                        <p className="text-muted" style={{ fontSize: '0.9rem' }}>
                          Next billing date: <strong>{periodEnd}</strong>
                        </p>
                      )}

                      {!isBillable && (
                        <p className="text-muted" style={{ fontSize: '0.9rem' }}>
                          {status === 'trial'
                            ? 'You are on the free trial. Choose a plan to keep going once your trial storm event is used.'
                            : 'There is no active subscription on this account.'}
                        </p>
                      )}

                      {billingActionError && (
                        <div className="alert alert-error" aria-live="polite">{billingActionError}</div>
                      )}

                      <div className="billing-actions">
                        {isBillable ? (
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleManageBilling}
                            disabled={billingAction !== null}
                          >
                            {billingAction === 'portal' ? 'Opening...' : 'Manage Billing'}
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => navigate('/pricing')}
                          >
                            View Plans
                          </button>
                        )}

                        {canCancel && (
                          <button
                            type="button"
                            className="btn btn-outline"
                            onClick={() => setShowCancelConfirm(true)}
                            disabled={billingAction !== null}
                          >
                            {billingAction === 'cancel' ? 'Cancelling...' : 'Cancel Subscription'}
                          </button>
                        )}
                      </div>

                      {isBillable && (
                        <p className="text-muted" style={{ fontSize: '0.85rem', marginTop: '12px', marginBottom: 0 }}>
                          Manage Billing opens Stripe&rsquo;s secure portal, where you can update
                          your card, download invoices and change plan. Enterprise plans are
                          arranged directly, so email{' '}
                          <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
                        </p>
                      )}
                    </>
                  );
                })()}
              </div>
              )}

            <div className="card mb-lg feature-request-card">
              <div className="card-header">
                <h2 className="card-title">Suggest a Feature</h2>
                <p className="text-muted" style={{ fontSize: '0.875rem', marginTop: '4px' }}>
                  Have an idea to improve {getAppName()}? Let us know!
                </p>
              </div>

              <form onSubmit={handleFeatureRequestSubmit}>
                {featureRequestError && <div className="alert alert-error" aria-live="polite">{featureRequestError}</div>}
                {featureRequestSuccess && <div className="alert alert-success" aria-live="polite">{featureRequestSuccess}</div>}

                <div className="form-group">
                  <label className="form-label" htmlFor="featureSubject">Subject</label>
                  <input
                    type="text"
                    id="featureSubject"
                    name="subject"
                    className="form-input"
                    value={featureRequest.subject}
                    onChange={handleFeatureRequestChange}
                    placeholder="Brief summary of your idea"
                    maxLength={255}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="featureDescription">Description</label>
                  <textarea
                    id="featureDescription"
                    name="description"
                    className="form-textarea"
                    value={featureRequest.description}
                    onChange={handleFeatureRequestChange}
                    rows={4}
                    placeholder="Describe your feature idea in detail..."
                  />
                </div>

                <button type="submit" className="btn btn-primary" disabled={featureRequestLoading}>
                  {featureRequestLoading ? 'Submitting...' : 'Submit Request'}
                </button>
              </form>

              {/* My Feature Requests */}
              {myFeatureRequests.length > 0 && (
                <div className="my-requests-section">
                  <h3 className="my-requests-title">My Feature Requests</h3>
                  {myFeatureRequestsLoading ? (
                    <div className="text-muted">Loading...</div>
                  ) : (
                    <div className="my-requests-list">
                      {myFeatureRequests.map(request => (
                        <div key={request.id} className="my-request-card">
                          <div className="my-request-header">
                            <span className="my-request-subject">{request.subject}</span>
                            <span className={`my-request-status status-${request.status}`}>
                              {request.status === 'new' && 'New'}
                              {request.status === 'under_review' && 'Under Review'}
                              {request.status === 'planned' && 'Planned'}
                              {request.status === 'in_progress' && 'In Progress'}
                              {request.status === 'completed' && 'Completed'}
                              {request.status === 'declined' && 'Declined'}
                            </span>
                          </div>
                          <div className="my-request-date">
                            Submitted: {new Date(request.created_at).toLocaleDateString('en-US', inZone(timezone, {
                              month: 'short', day: 'numeric', year: 'numeric'
                            }))}
                          </div>
                          <div className="my-request-description">
                            Your request: "{request.description}"
                          </div>
                          {request.admin_notes ? (
                            <div className="my-request-response">
                              <strong>Admin response:</strong> "{request.admin_notes}"
                            </div>
                          ) : (
                            <div className="my-request-awaiting">(Awaiting review)</div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

        {/* Delete Account, last in Account - account deletion (audit C6 / Finding 4.8a).
            Owner only. Google Play requires a user-initiated deletion path. */}
              {isOwner() && (
              <div className="card mb-lg danger-zone-card">
                <div className="card-header">
                  <h2 className="card-title danger-zone-title">Delete Account</h2>
                </div>

                <div className="danger-zone-warning">
                  <svg className="danger-zone-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <div>
                    <strong>This will permanently delete your company, all customers, routes,
                    services, expenses, and user accounts associated with this company. This
                    cannot be undone.</strong>
                  </div>
                </div>

                <p className="text-muted" style={{ fontSize: '0.875rem' }}>
                  Also deleted: every property, invoice, payment record, uploaded photo,
                  pricing tier, expense category and pending team invitation. Everyone on your
                  team loses access immediately. Export anything you need first, because there is
                  no grace period and no way to recover it.
                  {billing?.hasSubscription === true && (
                    <>
                      {' '}Your SnowTrack&trade; subscription is cancelled immediately when you
                      delete your account. You will not be charged again, and the rest of the
                      current billing period is not refunded.
                    </>
                  )}
                </p>

                {deleteAccountError && (
                  <div className="alert alert-error" aria-live="polite">{deleteAccountError}</div>
                )}

                <div className="form-group">
                  <label className="form-label" htmlFor="deleteAccountConfirm">
                    To confirm, type <strong>{company.name || user?.companyName || 'your company name'}</strong> below:
                  </label>
                  <input
                    type="text"
                    id="deleteAccountConfirm"
                    className="form-input danger-zone-input"
                    value={deleteAccountConfirm}
                    onChange={(e) => { setDeleteAccountConfirm(e.target.value); setDeleteAccountError(''); }}
                    placeholder="Type your company name to confirm"
                    autoComplete="off"
                  />
                </div>

                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleDeleteAccount}
                  disabled={
                    deleteAccountLoading ||
                    !(company.name || user?.companyName) ||
                    deleteAccountConfirm.trim() !== (company.name || user?.companyName || '').trim()
                  }
                >
                  {deleteAccountLoading ? 'Deleting Account...' : 'Delete My Account'}
                </button>

                <p className="text-muted" style={{ fontSize: '0.8rem', marginTop: '12px', marginBottom: 0 }}>
                  Would rather we did it? Email{' '}
                  <a href={`mailto:${SUPPORT_EMAIL}?subject=Account%20Deletion%20Request`}>{SUPPORT_EMAIL}</a>
                  {' '}or use the{' '}
                  <a href="/delete-account">account deletion request page</a>.
                </p>
              </div>
              )}
          </div>
        </div>

        <ConfirmModal
          show={showDisconnectConfirm}
          title="Disconnect Square?"
          message="New invoices will stop being sent from this account."
          confirmText="Yes, disconnect"
          cancelText="Keep it connected"
          confirmColor="#dc2626"
          onConfirm={handleDisconnectSquare}
          onCancel={() => setShowDisconnectConfirm(false)}
        />

        <ConfirmModal
          show={showCancelConfirm}
          title="Cancel your subscription?"
          message={
            billing?.currentPeriodEndDisplay
              ? `You'll keep full access until ${billing.currentPeriodEndDisplay}, then your account becomes read-only. You can still view your customers, history and reports, and you can resubscribe any time to run routes again.`
              : "You'll keep access until the end of the period you've paid for, then your account becomes read-only. You can still view your customers, history and reports, and you can resubscribe any time to run routes again."
          }
          confirmText="Yes, cancel it"
          cancelText="Keep my plan"
          confirmColor="#dc2626"
          onConfirm={handleCancelSubscription}
          onCancel={() => setShowCancelConfirm(false)}
        >
          <div className="form-group" style={{ marginBottom: '20px' }}>
            <label className="form-label" htmlFor="cancelReturnMonth">When do you expect to be back?</label>
            <select
              id="cancelReturnMonth"
              className="form-select"
              value={cancelReturnMonth}
              onChange={(e) => setCancelReturnMonth(e.target.value)}
            >
              <option value={NOT_SURE}>Not sure</option>
              {returnMonthOptions().map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </ConfirmModal>

      </div>
      <style>{`
        .app-header-content {
          overflow: hidden;
        }
        .header-nav {
          display: flex;
          align-items: center;
          gap: 4px;
          flex-shrink: 1;
          overflow: hidden;
        }
        .nav-link {
          background: transparent;
          border: none;
          color: rgba(255,255,255,0.8);
          padding: 8px 12px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 0.875rem;
          transition: all 0.15s ease;
          white-space: nowrap;
          flex-shrink: 0;
        }
        .nav-link:hover {
          background: rgba(255,255,255,0.1);
          color: white;
        }
        .nav-link.active {
          background: rgba(255,255,255,0.2);
          color: white;
        }
        .storm-link {
          background: var(--color-secondary) !important;
          color: white !important;
        }
        .storm-link:hover {
          background: var(--color-secondary-dark) !important;
        }
        .admin-link {
          background: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%) !important;
          color: white !important;
        }
        .admin-link:hover {
          background: linear-gradient(135deg, #6d28d9 0%, #5b21b6 100%) !important;
        }
        .logout-link {
          margin-left: 8px;
          border-left: 1px solid rgba(255,255,255,0.2);
          padding-left: 16px;
        }
        /* Mobile Menu */
        .mobile-nav {
          display: flex;
          flex-direction: column;
          padding: 8px 16px 16px;
          border-top: 1px solid rgba(255,255,255,0.1);
        }
        .mobile-nav-link {
          background: transparent;
          border: none;
          color: rgba(255,255,255,0.8);
          padding: 12px 8px;
          text-align: left;
          font-size: 1rem;
          cursor: pointer;
          border-radius: 6px;
          transition: all 0.15s ease;
        }
        .mobile-nav-link:hover, .mobile-nav-link.active {
          background: rgba(255,255,255,0.1);
          color: white;
        }
        .mobile-nav-link.storm-link {
          background: var(--color-secondary);
          color: white;
          margin-top: 8px;
        }
        .mobile-nav-link.admin-link {
          background: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%);
          color: white;
          margin-top: 8px;
        }
        .mobile-nav-link.logout-link {
          margin-top: 8px;
          border-top: 1px solid rgba(255,255,255,0.1);
          padding-top: 16px;
        }
        @media (max-width: 900px) {
          .desktop-nav {
            display: none;
          }
        }
        .subscription-info {
          display: flex;
          align-items: center;
          padding: 8px 0;
        }
        /* Account Banner Styles */
        .account-banner {
          background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%);
          border-radius: 12px;
          padding: 16px 20px;
          margin-bottom: 24px;
          color: white;
          box-shadow: 0 4px 12px rgba(37, 99, 235, 0.2);
        }
        .account-banner-name {
          font-size: 1.25rem;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 8px;
        }
        .account-icon {
          font-size: 1.1rem;
        }
        .account-banner-details {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 0.9rem;
          opacity: 0.9;
        }
        .account-detail {
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .detail-label {
          opacity: 0.8;
        }
        .detail-value {
          font-weight: 500;
        }
        .account-divider {
          opacity: 0.5;
        }
        @media (max-width: 480px) {
          .account-banner-details {
            flex-direction: column;
            align-items: flex-start;
            gap: 4px;
          }
          .account-divider {
            display: none;
          }
        }
        .pricing-section {
          margin-bottom: 24px;
        }
        .pricing-section-title {
          font-size: 1rem;
          font-weight: 600;
          color: var(--color-text);
          margin: 0 0 12px 0;
        }
        .pricing-table {
          border: 1px solid var(--color-border);
          border-radius: 8px;
          overflow: hidden;
        }
        .pricing-row {
          display: grid;
          grid-template-columns: 1fr 1fr 1fr;
          gap: 12px;
          padding: 12px 16px;
          border-bottom: 1px solid var(--color-border);
        }
        .pricing-row:last-child {
          border-bottom: none;
        }
        .pricing-row.pricing-header {
          background: var(--color-background);
          font-weight: 600;
          font-size: 0.875rem;
          color: var(--color-text-light);
        }
        .pricing-cell {
          display: flex;
          align-items: center;
        }
        .pricing-cell.tier-label {
          font-weight: 500;
        }
        .input-with-prefix {
          display: flex;
          align-items: center;
        }
        .input-with-prefix.compact {
          max-width: 120px;
        }
        .input-with-prefix .input-prefix {
          background: var(--color-background);
          padding: 0 8px;
          height: 36px;
          display: flex;
          align-items: center;
          border: 1px solid var(--color-border);
          border-right: none;
          border-radius: 6px 0 0 6px;
          color: var(--color-text-light);
          font-size: 0.875rem;
        }
        .input-with-prefix .form-input {
          border-radius: 0 6px 6px 0;
          height: 36px;
          padding: 0 8px;
        }
        @media (max-width: 768px) {
          .grid-2 {
            grid-template-columns: 1fr;
          }
          .pricing-row {
            grid-template-columns: 1fr;
            gap: 8px;
          }
          .pricing-row.pricing-header {
            display: none;
          }
          .pricing-cell.tier-label {
            font-weight: 600;
            margin-bottom: 4px;
          }
          .pricing-cell::before {
            content: attr(data-label);
            font-size: 0.75rem;
            color: var(--color-text-light);
            margin-right: 8px;
          }
        }

        /* Payment Methods */
        .payment-methods-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
          margin-bottom: 20px;
        }
        .payment-method-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 12px;
          padding: 12px 16px;
          background: #f9fafb;
          border-radius: 8px;
        }
        .payment-method-fee {
          display: flex;
          align-items: center;
          gap: 6px;
          margin-left: auto;
        }
        .payment-method-fee input[type="number"] {
          width: 70px;
          padding: 4px 8px;
        }
        .payment-method-fee .btn {
          margin-left: 8px;
        }
        .payment-method-toggle {
          display: flex;
          align-items: center;
          gap: 12px;
          cursor: pointer;
        }
        .payment-method-toggle input[type="checkbox"] {
          width: 20px;
          height: 20px;
          cursor: pointer;
        }
        .payment-method-name {
          font-weight: 500;
        }
        .built-in-badge {
          font-size: 0.7rem;
          background: #e5e7eb;
          color: #6b7280;
          padding: 2px 6px;
          border-radius: 4px;
          margin-left: 8px;
        }
        .btn-outline-danger {
          color: #ef4444;
          border-color: #ef4444;
          background: transparent;
        }
        .btn-outline-danger:hover {
          background: #fef2f2;
        }
        .add-payment-method {
          display: flex;
          gap: 12px;
          margin-bottom: 16px;
        }
        .add-payment-method .form-input {
          flex: 1;
          max-width: 300px;
        }

        /* Intake Form Link */
        .intake-link-section {
          margin-top: 16px;
        }
        .intake-link-row {
          display: flex;
          gap: 12px;
          margin-bottom: 8px;
        }
        .intake-link-input {
          flex: 1;
          font-family: monospace;
          font-size: 0.875rem;
          background: #f9fafb;
        }
        .intake-link-row .btn {
          white-space: nowrap;
        }

        /* Custom Slug Section */
        .custom-slug-section {
          margin-top: 24px;
          padding-top: 24px;
          border-top: 1px solid #e5e7eb;
        }
        .slug-description {
          font-size: 0.875rem;
          margin-bottom: 12px;
        }
        .slug-alert {
          margin-bottom: 12px;
          padding: 8px 12px;
          font-size: 0.875rem;
        }
        .slug-input-row {
          display: flex;
          align-items: center;
          gap: 0;
          flex-wrap: wrap;
          margin-bottom: 12px;
        }
        .slug-prefix {
          background: var(--color-bg-light, #f3f4f6);
          padding: 8px 12px;
          border: 1px solid #d1d5db;
          border-right: none;
          border-radius: 6px 0 0 6px;
          font-size: 0.875rem;
          color: #6b7280;
          white-space: nowrap;
        }
        .slug-input-wrapper {
          flex: 1;
          min-width: 150px;
          position: relative;
          display: flex;
          align-items: center;
        }
        .slug-input {
          border-radius: 0 6px 6px 0 !important;
          font-family: monospace;
          width: 100%;
        }
        .slug-input.input-success {
          border-color: #10b981;
        }
        .slug-input.input-error {
          border-color: #ef4444;
        }
        .slug-status {
          position: absolute;
          right: 10px;
          font-size: 0.75rem;
          font-weight: 500;
        }
        .slug-status.checking {
          color: #6b7280;
        }
        .slug-status.available {
          color: #10b981;
        }
        .slug-status.taken {
          color: #ef4444;
        }
        .slug-actions {
          display: flex;
          gap: 12px;
          margin-bottom: 12px;
        }
        .slug-help {
          display: block;
          font-size: 0.8rem;
        }
        @media (max-width: 600px) {
          .slug-input-row {
            flex-direction: column;
            align-items: stretch;
          }
          .slug-prefix {
            border-radius: 6px 6px 0 0;
            border-right: 1px solid #d1d5db;
            border-bottom: none;
            text-align: center;
          }
          .slug-input {
            border-radius: 0 0 6px 6px !important;
          }
        }

        /* Lead Source Stats */
        .lead-source-stats {
          border-top: 1px solid #e5e7eb;
          padding-top: 20px;
        }
        .stats-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        }
        .stats-header h3 {
          margin: 0;
          font-size: 1rem;
          font-weight: 600;
          color: #374151;
        }
        .stats-period-select {
          width: auto;
          padding: 6px 12px;
          font-size: 0.875rem;
        }
        .stats-loading, .stats-empty {
          text-align: center;
          color: #6b7280;
          padding: 20px;
          font-size: 0.9rem;
        }
        .stats-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .stats-item {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .stats-item-info {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .stats-item-name {
          font-size: 0.9rem;
          color: #374151;
        }
        .stats-item-count {
          font-size: 0.85rem;
          color: #6b7280;
          font-weight: 500;
        }
        .stats-bar-container {
          height: 8px;
          background: #e5e7eb;
          border-radius: 4px;
          overflow: hidden;
        }
        .stats-bar {
          height: 100%;
          background: linear-gradient(90deg, #3b82f6, #8b5cf6);
          border-radius: 4px;
          transition: width 0.3s ease;
        }
        .stats-total {
          margin-top: 8px;
          padding-top: 12px;
          border-top: 1px solid #e5e7eb;
          font-size: 0.85rem;
          color: #6b7280;
          text-align: right;
        }

        /* Pricing Tiers */
        .pricing-tiers-list {
          display: flex;
          flex-direction: column;
          gap: 16px;
          margin-bottom: 20px;
        }
        .pricing-tier-item {
          padding: 16px;
          background: #f9fafb;
          border-radius: 8px;
          border: 1px solid #e5e7eb;
        }
        .tier-row {
          display: flex;
          gap: 12px;
          align-items: flex-end;
        }
        .tier-field {
          flex: 1;
        }
        .tier-field-name {
          flex: 2;
        }
        .tier-field-price {
          flex: 1;
          min-width: 100px;
        }
        .tier-field .form-label {
          margin-bottom: 4px;
          font-size: 0.8rem;
          color: #6b7280;
        }
        .tier-actions {
          display: flex;
          align-items: center;
          padding-bottom: 2px;
          flex-shrink: 0;
        }
        .pricing-tiers-actions {
          display: flex;
          gap: 12px;
          margin-top: 16px;
        }
        .inactive-tiers-section {
          margin-top: 20px;
          padding: 16px;
          background: #fef3c7;
          border-radius: 8px;
          border: 1px solid #fcd34d;
        }
        .inactive-tiers-title {
          margin: 0 0 8px 0;
          font-size: 0.9rem;
          color: #92400e;
        }
        .inactive-tier-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 8px 0;
          border-bottom: 1px solid #fcd34d;
        }
        .inactive-tier-item:last-child {
          border-bottom: none;
        }
        .inactive-tier-name {
          color: #92400e;
          font-size: 0.9rem;
        }
        @media (max-width: 600px) {
          .tier-row {
            flex-direction: column;
            align-items: stretch;
            gap: 12px;
          }
          .tier-actions {
            justify-content: flex-end;
          }
          .pricing-tiers-actions {
            flex-direction: column;
          }
        }

        .mt-md {
          margin-top: 16px;
        }
        .mb-md {
          margin-bottom: 16px;
        }

        /* Feature Request */
        .feature-request-card {
          background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
          border: 1px solid #bae6fd;
        }
        .feature-request-card .card-title {
          color: #0369a1;
        }
        .feature-request-card .form-textarea {
          min-height: 100px;
          resize: vertical;
        }

        /* My Feature Requests */
        .my-requests-section {
          margin-top: 24px;
          padding-top: 24px;
          border-top: 1px solid #bae6fd;
        }
        .my-requests-title {
          font-size: 1rem;
          font-weight: 600;
          color: #0369a1;
          margin: 0 0 16px 0;
        }
        .my-requests-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .my-request-card {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 12px 16px;
        }
        .my-request-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 6px;
        }
        .my-request-subject {
          font-weight: 600;
          color: #1f2937;
        }
        .my-request-status {
          font-size: 0.75rem;
          font-weight: 600;
          padding: 3px 10px;
          border-radius: 12px;
        }
        .my-request-status.status-new {
          background: #dbeafe;
          color: #1e40af;
        }
        .my-request-status.status-under_review {
          background: #fef3c7;
          color: #92400e;
        }
        .my-request-status.status-planned {
          background: #ede9fe;
          color: #6d28d9;
        }
        .my-request-status.status-in_progress {
          background: #ffedd5;
          color: #c2410c;
        }
        .my-request-status.status-completed {
          background: #d1fae5;
          color: #065f46;
        }
        .my-request-status.status-declined {
          background: var(--color-bg-light, #f3f4f6);
          color: #6b7280;
        }
        .my-request-date {
          font-size: 0.8rem;
          color: #6b7280;
          margin-bottom: 8px;
        }
        .my-request-description {
          font-size: 0.875rem;
          color: #4b5563;
          margin-bottom: 8px;
          font-style: italic;
        }
        .my-request-response {
          font-size: 0.875rem;
          background: #f0fdf4;
          border: 1px solid #bbf7d0;
          border-radius: 6px;
          padding: 8px 12px;
          color: #166534;
        }
        .my-request-awaiting {
          font-size: 0.8rem;
          color: #9ca3af;
          font-style: italic;
        }
        /* Radio button styles */
        .radio-label {
          display: flex;
          align-items: center;
          gap: 10px;
          cursor: pointer;
          padding: 8px 0;
          user-select: none;
        }
        .radio-label input[type="radio"] {
          width: 18px;
          height: 18px;
          accent-color: var(--color-primary);
          cursor: pointer;
        }
        .radio-text {
          font-size: 0.95rem;
          color: var(--color-text);
        }
        /* Disclaimer Settings */
        .disclaimer-preview {
          margin-top: 16px;
          margin-bottom: 16px;
        }
        .disclaimer-preview-box {
          background: #f9fafb;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 16px;
          font-size: 0.875rem;
          color: #4b5563;
          line-height: 1.5;
        }
        .disclaimer-actions {
          display: flex;
          gap: 12px;
          margin-top: 16px;
        }

        /* Collapsible Sections */
        .settings-section {
          margin-bottom: 24px;
          background: white;
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          overflow: hidden;
        }
        .settings-section-header {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 16px 20px;
          background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-dark) 100%);
          color: #ffffff;
          cursor: pointer;
          user-select: none;
          transition: background 0.2s ease;
        }
        .settings-section-header:hover {
          background: linear-gradient(135deg, var(--color-primary-dark) 0%, var(--color-primary) 100%);
        }
        .section-arrow {
          font-size: 0.875rem;
          width: 16px;
          transition: transform 0.2s ease;
          color: #ffffff;
        }
        .section-title {
          font-size: 1.125rem;
          font-weight: 600;
          margin: 0;
          color: #ffffff;
        }
        .settings-section-content {
          max-height: 0;
          overflow: hidden;
          transition: max-height 0.3s ease-out;
        }
        .settings-section.expanded .settings-section-content {
          max-height: 5000px;
          transition: max-height 0.5s ease-in;
        }
        .settings-section-content > .card {
          border-radius: 0;
          box-shadow: none;
          border-bottom: 1px solid var(--color-border);
        }
        .settings-section-content > .card:last-child {
          border-bottom: none;
        }
        .settings-section-content > .card.mb-lg {
          margin-bottom: 0 !important;
        }
        @media (max-width: 768px) {
          .settings-section-header {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 16px 20px;
          background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-dark) 100%);
          color: #ffffff;
          cursor: pointer;
          user-select: none;
          transition: background 0.2s ease;
        }
          .section-title {
            font-size: 1rem;
          }
        }

        /* Team Management Styles */
        .team-list {
          display: flex;
          flex-direction: column;
        }

        .team-member-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 20px;
          border-bottom: 1px solid #e5e7eb;
        }

        .team-member-row:last-child {
          border-bottom: none;
        }

        .team-member-info {
          flex: 1;
        }

        .team-member-name {
          font-weight: 500;
          color: #1e293b;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .you-badge {
          font-size: 0.75rem;
          color: #64748b;
          font-weight: 400;
        }

        .team-member-email {
          font-size: 0.875rem;
          color: #64748b;
          margin-top: 2px;
        }

        .team-member-actions {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .role-select {
          width: auto;
          padding: 6px 12px;
          font-size: 0.875rem;
        }

        .confirm-remove {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 0.875rem;
        }

        .confirm-remove span {
          color: var(--color-error);
          font-weight: 500;
        }

        /* Role Badges */
        .badge-owner {
          background: #f3e8ff;
          color: #7c3aed;
        }

        .badge-admin {
          background: #dbeafe;
          color: #1d4ed8;
        }

        .badge-crew-lead {
          background: #dcfce7;
          color: #15803d;
        }

        .badge-driver {
          background: var(--color-bg-light, #f3f4f6);
          color: #4b5563;
        }

        /* Pending Invitations */
        .pending-invitations {
          border-top: 2px solid #e5e7eb;
          padding-top: 16px;
          margin-top: 8px;
        }

        .subsection-title {
          font-size: 0.875rem;
          font-weight: 600;
          color: #64748b;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          padding: 0 20px;
          margin-bottom: 8px;
        }

        .invitation-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px 20px;
          background: #fafafa;
        }

        .invitation-row:nth-child(odd) {
          background: #f5f5f5;
        }

        .invitation-email {
          font-weight: 500;
          color: #1e293b;
        }

        .invitation-meta {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-top: 4px;
        }

        .invitation-expires {
          font-size: 0.75rem;
          color: #94a3b8;
        }

        /* Modal Styles */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
        }

        .modal {
          background: white;
          border-radius: 12px;
          width: 100%;
          max-width: 480px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 24px;
          border-bottom: 1px solid #e5e7eb;
        }

        .modal-header h2 {
          font-size: 1.25rem;
          font-weight: 600;
          color: #1e293b;
          margin: 0;
        }

        .modal-close {
          background: none;
          border: none;
          font-size: 1.5rem;
          color: #64748b;
          cursor: pointer;
          padding: 4px 8px;
          line-height: 1;
        }

        .modal-close:hover {
          color: #1e293b;
        }

        .modal form {
          padding: 24px;
        }

        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          padding-top: 16px;
          margin-top: 8px;
          border-top: 1px solid #e5e7eb;
        }

        /* Button variations */
        .btn-sm {
          padding: 6px 12px;
          font-size: 0.8125rem;
        }

        .btn-error {
          background: var(--color-error);
          color: white;
          border: none;
        }

        .btn-error:hover {
          background: #b91c1c;
        }

        @media (max-width: 640px) {
          .team-member-row {
            flex-direction: column;
            align-items: flex-start;
            gap: 12px;
          }

          .team-member-actions {
            width: 100%;
            justify-content: flex-start;
          }

          .invitation-row {
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
          }
        }
        .addon-price-input-wrapper {
          display: flex;
          align-items: center;
          gap: 2px;
        }
        .addon-price-input-wrapper .price-prefix {
          color: #64748b;
          font-size: 0.85rem;
        }
        .addon-price-input {
          padding: 4px 6px !important;
        }
        .inactive-addons-section {
          margin-top: 16px;
          padding-top: 16px;
          border-top: 1px dashed #e2e8f0;
        }
        .inactive-addons-section .inactive-label {
          font-size: 0.85rem;
          color: #94a3b8;
          margin-bottom: 8px;
        }
        .inactive-addons-section .service-addon-item {
          display: flex;
          align-items: center;
          padding: 8px 0;
          color: #94a3b8;
        }
        .addon-tier-grid { overflow-x: auto; margin-bottom: 16px; }
        .addon-tier-header, .addon-tier-row { display: flex; align-items: center; gap: 8px; padding: 8px 0; min-width: fit-content; }
        .addon-tier-header { font-weight: 600; font-size: 0.85rem; color: #64748b; border-bottom: 2px solid #e2e8f0; padding-bottom: 12px; margin-bottom: 4px; }
        .addon-tier-row { border-bottom: 1px solid #f1f5f9; }
        .addon-tier-row-new { border-bottom: none; padding-top: 12px; border-top: 2px dashed #e2e8f0; }
        .addon-col-toggle { width: 32px; flex-shrink: 0; }
        .addon-col-name { width: 160px; flex-shrink: 0; }
        .addon-col-name .form-input { width: 100%; }
        .addon-col-price { width: 100px; flex-shrink: 0; }
        .addon-col-tier { width: 100px; flex-shrink: 0; }
        .addon-col-actions { width: 70px; flex-shrink: 0; }
        .addon-col-tier .addon-price-input, .addon-col-price .addon-price-input { width: 80px; }
        .addon-price-input:disabled { background: #f1f5f9; color: #94a3b8; }
        .addon-tier-row-main { border-bottom: none; padding-bottom: 0; }
        .addon-tier-options-row { padding: 4px 0 10px 40px; border-bottom: 1px solid #f1f5f9; }
        .addon-tier-options-row-new { border-bottom: none; }
        .addon-fee-options { display: flex; flex-wrap: wrap; align-items: center; gap: 8px 16px; font-size: 0.85rem; color: #475569; }
        .addon-fee-option { display: inline-flex; align-items: center; gap: 6px; margin: 0; }
        .addon-fee-type { width: auto; padding: 4px 6px !important; }
        .addon-fee-percent { width: 70px; }
        .addon-fee-hint { color: #64748b; }
        .addon-fee-detail { width: 170px; padding: 4px 6px !important; }
        .addon-help { font-size: 0.85rem; }
        @media (max-width: 640px) {
          .addon-tier-grid { font-size: 0.85rem; }
          .addon-col-name { width: 120px; }
          .addon-col-tier { width: 85px; }
          .addon-col-price { width: 85px; }
        }

        /* SnowTrack subscription */
        .billing-summary {
          display: flex;
          flex-wrap: wrap;
          gap: 32px;
          margin-bottom: 16px;
        }

        .billing-row {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .billing-label {
          font-size: 0.75rem;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          color: #6b7280;
          font-weight: 600;
        }

        .billing-value {
          font-size: 1.25rem;
          font-weight: 700;
          color: #111827;
        }

        /* Over a plan limit. Amber, not red: nothing is broken and nothing has
           been taken away, but the owner cannot add until they have room. */
        .billing-value-over {
          color: #b45309;
          font-size: 1rem;
        }

        .billing-status {
          display: inline-block;
          padding: 4px 12px;
          border-radius: 999px;
          font-size: 0.8rem;
          font-weight: 600;
          background: #f3f4f6;
          color: #374151;
          width: fit-content;
        }

        .billing-status-active,
        .billing-status-trialing {
          background: #d1fae5;
          color: #065f46;
        }

        .billing-status-past_due {
          background: #fee2e2;
          color: #991b1b;
        }

        .billing-status-cancelled,
        .billing-status-canceled,
        .billing-status-expired {
          background: #fef3c7;
          color: #92400e;
        }

        .billing-actions {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
          margin-top: 16px;
        }

        /* Danger Zone - account deletion */
        .danger-zone-card {
          border: 1px solid #fecaca;
          border-top: 3px solid #dc2626;
        }

        .danger-zone-title {
          color: #b91c1c;
        }

        .danger-zone-warning {
          display: flex;
          gap: 12px;
          align-items: flex-start;
          background: #fef2f2;
          border: 1px solid #fecaca;
          border-radius: 8px;
          padding: 14px 16px;
          margin-bottom: 16px;
          color: #7f1d1d;
          line-height: 1.55;
        }

        .danger-zone-icon {
          width: 22px;
          height: 22px;
          flex-shrink: 0;
          color: #dc2626;
          margin-top: 2px;
        }

        .danger-zone-input {
          border-color: #fca5a5;
        }

        .danger-zone-input:focus {
          border-color: #dc2626;
          box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.12);
        }
      `}</style>
    </div>
  );
}

export default Settings;
